import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ModuleInfoButton } from '../components/ModuleInfoButton';
import { ChainRail } from '../features/traceability/ChainRail';
import { InvoiceHeader } from '../features/clientInvoice/InvoiceHeader';
import ActionDialog from '../components/ActionDialog';
import { DeliveryTab } from '../features/clientInvoice/DeliveryTab';
import { DELIVERY_FIELD_NOTES } from '../features/clientInvoice/deliveryFieldNotes';
import { DetailsTab } from '../features/clientInvoice/DetailsTab';
import { addBillVendorRow } from '../features/clientInvoice/billVendorRow';
import { SET_ON_THIS_LINE_NOTE } from '../features/clientInvoice/costSourceLabels';
import { ProfitabilityTab } from '../features/clientInvoice/ProfitabilityTab';
import { buildProfitability } from '../features/clientInvoice/profitabilityRows';
import { orderProgress, type EnteredCost } from '../features/clientInvoice/orderProgress';
import { OrderProgressStrip } from '../features/clientInvoice/OrderProgressStrip';
import { fetchLiveProgress, type LiveProgress } from '../lib/clientInvoiceProgressApi';
import { useLatestRequest } from '../hooks/useLatestRequest';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { newCreditNotePath } from '../features/clientInvoice/creditNoteLink';
import { creditStatusFor } from '../features/clientInvoice/creditStatus';
import LoadingState from '../components/LoadingState';
import { Modal } from '../components/Modal';
import { IssuesBar } from '../features/clientInvoice/IssuesBar';
import { NotSuppliedPanel } from '../features/clientInvoice/NotSuppliedPanel';
import { LinesGrid } from '../features/clientInvoice/LinesGrid';
import { LineDrawer, type DrawerIssue, type SourceQtyField } from '../features/clientInvoice/LineDrawer';
import { quoteInfoFor } from '../features/clientInvoice/quoteInfo';
import { alertsOf, analyseInvoice, hasBlockingIssue, lineStatus, unpickedSupplierLines } from '../features/clientInvoice/lineIssues';
import { fetchConfirmedOrderById } from '../lib/confirmedOrderApi';
import { searchMasterCatalogue } from '../lib/masterCatalogueApi';
import {
  ClientInvoiceDto,
  ClientInvoiceItemDto,
  createOrGetClientInvoiceFromConfirmedOrder,
  createClientInvoiceDeliverySlip,
  fetchClientInvoiceById,
  fetchClientInvoicePdf,
  fetchClientInvoicePackingListPdf,
  fetchClientInvoiceNotSuppliedPdf,
  fetchClientInvoiceDeliverySlipPdf,
  fetchClientInvoiceSlipPackingListPdf,
  fetchClientInvoiceCustomsSlipPdf,
  fetchClientInvoiceByConfirmedOrderId,
  patchClientInvoice,
  convertClientInvoiceCurrency,
  type ClientInvoicePatch,
  resolveUomFactor,
  createAdditionalClientInvoice,
  writebackProcurementPlanLine,
  createClientInvoiceExchangeSlip,
  syncClientInvoiceAlerts,
  dismissClientInvoiceAlert,
  payerOf,
  listClientInvoiceVendorBills,
  fetchVendorBillForImport,
  type DraftLineForPairing,
  type VendorBillForImport,
  type VendorBillLinePair,
} from '../lib/clientInvoiceApi';
import { openPrintInNewTab } from '../lib/serverPrint';
import type { CandidateInvoice } from '../lib/reconciliationApi';
import { CandidateInvoiceCells } from '../features/reconciliation/CandidateInvoiceRow';
import { CreditNote, getCreditNotesForInvoice } from '../lib/creditNoteApi';
import { getInventoryItems } from '../lib/inventoryApi';
import { usePermission } from '../hooks/usePermission';
import { useDraftHistory } from '../hooks/useDraftHistory';
import { useToast } from '../context/ToastContext';
import { useColumnWidths } from '../hooks/useColumnWidths';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { canMoveClientInvoiceStatus } from '../utils/clientInvoiceStatus';
// The owner's configured TT$/US$ rate (BUSINESS_RULES#fx-rate-ttd-usd) — the
// last rung of the D119.9 ladder (`ttdCostRate`): no rate on the line or the invoice.
import { getExchangeRate } from '../lib/settingsApi';
import {
  fetchSupplierDropdownDataFromAllTables,
  type SupplierDropdownItem,
} from '../lib/vendorInvoiceApi';
import { formatDate as fmtDate, formatDateTime as fmtDateTime, storedDay } from "../utils/dateFormat";
import { downloadRowsCsv } from '../utils/downloadCsv';
import { clientDisplayName, orderRef } from '../utils/orderIdentity';
import { resolveVatAmount } from '../utils/lineTax';
import { describeApiError } from '../utils/errorPresentation';
import type { ExchangeAnswers, WritebackAnswers } from '../features/clientInvoice/worksheetPrompts';
import { DraftInput, parseCellText } from '../features/clientInvoice/DraftInput';
import { fetchInvoicePinnedRate } from '../lib/clientInvoiceMarginApi';
import { remainingDispatchQty, shortDeliveryCreditedQtyByLine, slipRowsForLine } from '../utils/clientInvoiceDispatch';
import { num, round2 } from '../utils/invoiceLineMath';
import { costToUsdExact, usdToTtd } from '../utils/fxDisplay';
import { formatCurrency } from '../utils/priceUtils';
import { sellingPriceAt } from '../utils/pricing';
import { buildClientInvoiceSummary } from '../utils/clientInvoiceSummary';
import { CUSTOMS_CATEGORY } from '../utils/invoiceCategories';
import { billedQuantity, billedUnitPrice, isMarkedNotSupplied, isOrderDerivedLine, lineMarkup, priceClientInvoiceLine } from '../utils/clientInvoiceLine';
import { invoiceHasMoney } from '../utils/clientInvoiceCurrency';
import { browserToday } from '../features/vendorInvoices/paymentOnReceiptForm';
import { resolveUomCascadeCost, resolveImportedLineCost, importedSellingPrice, mergedBillLines, costInputText, ttdCostRate, costEntryFor, type TtdRate } from '../features/clientInvoice/lineCostCurrency';
import {
  buildInvoiceTotalsRows,
  canEditInvoiceCharges,
  isInvoiceSentOnward,
  type InvoiceChargeRow,
} from '../features/clientInvoice/invoiceTotals';

type TabKey = 'invoice' | 'delivery' | 'profitability' | 'details';
type LineItemSource = 'inventory' | 'catalogue' | 'supplier';

type LineItemSuggestion = {
  key: string;
  source: LineItemSource;
  title: string;
  subtitle: string;
  description: string;
  unit: string;
  price: number | null;
  /** The source's ISO currency; '' = it states none, and its price is not imported (D157.5). */
  currency: string;
  supplier?: string;
  /** D86: the vendor the source row names by id (a catalogue row vendorId); absent when the source has only a name. */
  supplierVendorId?: string | null;
  inventoryItemId?: string;
  sourceRef?: {
    provider?: string;
    externalId?: string;
  };
};

type FeedbackModalState = {
  open: boolean;
  tone: 'success' | 'error';
  title: string;
  message: React.ReactNode;
};

const PAGE_INFO = (
  <div className="flex flex-col gap-3">
    <p>Invoice preparation flow for a confirmed order.</p>
    <p className="font-medium">Sections:</p>
    <ul className="list-disc list-inside space-y-1 pl-1">
      <li>Invoice: discrepancy alerts, PO matches, editable quantities, and profitability</li>
      <li>Delivery Slip: split dispatch slips, packing list, and delivery tracking</li>
      <li>Drag a column's edge to resize it; your widths are saved. A column with nothing on any line is not shown.</li>
    </ul>
  </div>
);

// D96.14: no silent default - '' means "not chosen yet". The invoice's OWN currency only (top-level first): the
// confirmed order's schema default is 'USD' and borrowing it showed a currency the invoice never had.
const invoiceCurrencyOf = (inv?: { currency?: string; invoiceSection?: { currency?: string } | null } | null) =>
  inv?.currency || inv?.invoiceSection?.currency || '';

function money(value: number | null | undefined, currency = '') {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  // D96.14: an invoice with no currency yet shows a bare number, never a guessed symbol.
  // formatCurrency (utils/priceUtils) is the labelled-money owner and never throws: a stored code like
  // 'TT$' made Intl.NumberFormat throw RangeError and blank the page. null, not '': an empty code is falsy.
  return formatCurrency(value, currency || null, { ceil: false });
}

/** A PO match's quantity with the PO line's unit ("4 CS") — since D82 it may be packs
 *  beside a PCS invoice line. A match stored before 2026-09-28 has no unit: the bare number. */
function poMatchQuantity(match: { quantity?: number; uom?: string }) {
  return `${match.quantity ?? 0}${match.uom ? ` ${match.uom}` : ''}`;
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

// yyyy-mm-dd, for STORING and for <input type="date">, which accepts nothing
// else. Not for display — dates are shown dd/mm/yyyy via fmtDate. The two used
// to be the same function, so the value written to the record and the value
// printed on the invoice were forced to share a format.
function toDateInputValue(value?: string) {
  return storedDay(value) ?? '';
}

function addDays(value: string, days: number) {
  const d = new Date(value || new Date());
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatEditableNumber(value: unknown) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toFixed(2);
}


function getRowPricing(item: ClientInvoiceItemDto) {
  const confirmedQty = toNumber(item.confirmedQuantity, 0);
  const invoiceQty = toNumber(item.invoiceQuantity ?? confirmedQty, confirmedQty);
  const confirmedQtyUnit = String(item.unit || '').trim();
  const invoiceQtyUnit = String(item.billUnit || item.packingUnit || item.unit || '').trim();
  const costPrice =
    item.actualUnitCost != null
      ? toNumber(item.actualUnitCost, 0)
      : item.combinedUnitCost != null
        ? toNumber(item.combinedUnitCost, 0)
        : null; // no cost is no cost (D96.8) - never the selling price, which read as 0% markup
  const sellingPrice =
    item.quoteUnitPrice != null
      ? toNumber(item.quoteUnitPrice, 0)
      : billedUnitPrice(item);
  // The ACTUAL mark-up (what the sale earned over cost) - the CSV's "Actual mark-up %". ONE figure everywhere it is
  // shown: the per-line one buildDraftItem stores (priceClientInvoiceLine), the one Profitability and J7 use (D119.15).
  const actualMarkupPercent = num(item.markupPercent);
  // D119.15: the cell shows the typed rate while it still produces the price (the owner judges), else the actual %.
  const markup = lineMarkup({ actualUnitCost: costPrice, quoteUnitPrice: sellingPrice, typedMarkupPercent: item.typedMarkupPercent });
  const markupPercent = markup?.basis === 'typed' ? markup.percent : actualMarkupPercent;
  // What the sale really makes, stated beside a typed rate when the two differ by a cent of a percent or more.
  const makesMarkupPercent =
    markup?.basis === 'typed' && actualMarkupPercent != null && Math.abs(actualMarkupPercent - markup.percent) >= 0.01
      ? actualMarkupPercent
      : null;
  // The billed money the line stores (buildDraftItem keeps it / re-prices it on an edit) — never
  // re-extended here from the price, which would show 11.00 over a stored 12.50 (#303).
  const lineTotal = item.invoiceLineTotal == null ? null : toNumber(item.invoiceLineTotal, 0);
  // 2026-09-22 one-owner audit — vessels are never charged VAT
  // (BUSINESS_RULES#client-invoice-line-vat). Forced here, not read off the
  // item: a stored pre-2026-09-22 invoice can still carry an old nonzero
  // vatApplied/vatPercent (D45.12 — nothing here backfills stored data), and
  // this is a live recomputation, not a record of what was billed.
  const vatApplied = false;
  const vatPercent = 0;
  const vatAmount = resolveVatAmount({ vatApplied, vatPercent, taxableBase: lineTotal });

  return {
    confirmedQty,
    invoiceQty,
    confirmedQtyUnit,
    invoiceQtyUnit,
    costPrice,
    sellingPrice,
    markupPercent,
    actualMarkupPercent,
    makesMarkupPercent,
    lineTotal,
    vatApplied,
    vatPercent,
    vatAmount,
  };
}

// D96.9 - why a line cannot be removed (the server refuses it too: CLIENT_INVOICE_LINE_REMOVAL_LOCKED).
// A line that was delivered, or that a non-cancelled credit note points at, is keyed to its number.
function lineRemovalLock(item: ClientInvoiceItemDto, index: number, creditNotes: CreditNote[]): string | null {
  if (toNumber(item.dispatchedQuantity, 0) > 0) return 'This line has been delivered and cannot be removed - raise a credit note to correct it.';
  return lineCredited(item, index, creditNotes) ? 'A credit note has been raised against this line, so it cannot be removed.' : null;
}

// A non-cancelled credit note names this line (by its number).
function lineCredited(item: ClientInvoiceItemDto, index: number, creditNotes: CreditNote[]): boolean {
  const lineNumber = toNumber(item.lineNumber, index + 1);
  return creditNotes.some(
    (cn) => cn.status !== 'cancelled' && (cn.items || []).some((it) => it.sourceInvoiceLineNumber != null && Number(it.sourceInvoiceLineNumber) === lineNumber)
  );
}

function nextLineNumber(items: ClientInvoiceItemDto[]) {
  return items.reduce((max, item, index) => {
    const current = Number(item.lineNumber ?? index + 1);
    return Number.isFinite(current) && current > max ? current : max;
  }, 0) + 1;
}

function normalizeLineItemSuggestion(
  source: LineItemSource,
  item: any
): LineItemSuggestion {
  if (source === 'inventory') {
    const supplier =
      String(
        item?.supplier ||
          item?.vendorName ||
          item?.lastSupplierName ||
          item?.costing?.lastSupplierName ||
          item?.origin?.supplier ||
          ''
      ).trim();
    // Missing is null, never 0 (num(): Number(null) / Number('') are 0).
    const price = num(item?.priceTT) ?? num(item?.costPerUnit);
    return {
      key: String(item?._id || item?.id || item?.name || Math.random()),
      source,
      title: String(item?.name || item?.sku || 'Inventory Item'),
      subtitle: [item?.category, item?.subCategory].filter(Boolean).join(' / ') || item?.packaging || '',
      description: String(item?.sku || item?.packaging || ''),
      unit: String(item?.unit || item?.baseUnit || ''),
      price,
      currency: String(item?.currency || 'TTD'),
      supplier,
      inventoryItemId: String(item?._id || item?.id || ''),
      sourceRef: {
        provider: String(item?.sourceRef?.provider || 'inventory'),
        externalId: String(item?.sourceRef?.externalId || item?._id || item?.id || ''),
      },
    };
  }

  const supplier =
    String(
      item?.supplier ||
        item?.vendorName ||
        item?.selectedVendor ||
        item?.origin?.supplier ||
        item?.origin?.vendorName ||
        ''
    ).trim();
  const price = num(item?.item_price);
  return {
    key: String(item?._id || item?.id || item?.item_name || Math.random()),
    source,
    title: String(item?.item_name || 'Catalogue Item'),
    subtitle: [item?.category, item?.sub_category].filter(Boolean).join(' / ') || item?.supplier || '',
    description: String(item?.supplier || item?.origin?.docCollection || ''),
    unit: String(item?.unit || ''),
    price,
    currency: String(item?.currency || 'TTD'),
    supplier,
    supplierVendorId: item?.vendorId ? String(item.vendorId) : null,
  };
}

function normalizeSupplierBillSuggestion(item: SupplierDropdownItem): LineItemSuggestion {
  const supplier = String(item.vendorName || '').trim();
  return {
    key: `${item.vendorName || 'vendor'}:${item.description || Math.random()}`,
    source: 'supplier',
    title: String(item.description || 'Vendor Bill Item'),
    subtitle: String(item.vendorName || ''),
    description: String(item.description || ''),
    unit: String(item.uom || ''),
    price: num(item.unitPrice),
    // The server-resolved ISO code (`supplierOptions.service`); null - a bare "$" row - stays none,
    // so the pick refuses its price instead of reading it as TT$ (D157.5).
    currency: item.currency ?? '',
    supplier,
    // D170.9: the vendor the option is filed under (a linked bill or catalogue row), else null.
    supplierVendorId: item.vendorId ? String(item.vendorId) : null,
  };
}

function buildLineItemFromVendorInvoice(
  vendorInvoice: VendorBillForImport,
  lineItem: any,
  /** D171.1: the line's quantity, unit and price per it as reviewed (the server's pair), the figures the dialog shows and a merge bills. */
  pair: VendorBillLinePair,
  lineNumber: number,
  invoiceCurrency: string,
  /** D157.5: the D119.9 ladder's rate (`ttdCostRate`) a cross-currency bill price converts at. */
  rate: TtdRate
): ClientInvoiceItemDto {
  const { supplierName, supplierVendorId } = billSupplier(vendorInvoice);
  const description = String(lineItem?.description || lineItem?.itemName || lineItem?.name || 'Vendor Bill Item');
  const qty = Math.max(0, pair.quantity ?? 0);
  const uom = pair.unit;
  const unitPrice = pair.unitPrice;
  // 2026-09-22 one-owner audit — vessels are never charged VAT
  // (BUSINESS_RULES#client-invoice-line-vat). This used to read the vendor
  // bill's own taxRate/taxAmount (input VAT — USC's cost, owned elsewhere) onto
  // the new CLIENT invoice line as if it were output VAT charged to the
  // vessel. It never carries over: `buildDraftItem` below forces every
  // client-invoice line's VAT to false/0/0 regardless of what is passed in, so
  // this line simply omits vatApplied/vatPercent/vatAmount rather than compute
  // a figure that is thrown away.

  // `unitPrice` is the line's price as reviewed, per `uom`, in the bill's OWN currency as the server states it (null:
  // none, so no cost and the refusal note, D157.5 / D171.1).
  const { cost, refusalReason } = resolveImportedLineCost({
    invoiceCurrency,
    sourceCurrency: vendorInvoice.currency,
    sourcePrice: unitPrice,
    rate,
  });
  const internalRemarks = refusalReason ? billImportNote(refusalReason) : '';

  return buildDraftItem({
    lineNumber,
    confirmedOrderItemIndex: null,
    sourceType: 'supplier',
    supplierName,
    supplierVendorId,
    itemName: description,
    itemDescription: description,
    confirmedQuantity: qty,
    invoiceQuantity: qty,
    billQuantity: qty,
    billUnit: uom,
    unit: uom,
    quoteUnitPrice: importedSellingPrice(cost), // D119.10: whole-cent price from the EXACT cost below
    actualUnitCost: cost,
    ...(cost != null ? personCostBasis('chosen') : {}), // D157.9: set on this line, the bill paired with nothing
    poMatches: [],
    discrepancyAlert: '',
    internalRemarks,
    packingQuantity: qty,
    packingUnit: uom,
    dispatchedQuantity: 0,
    dispatchQuantity: 0,
  });
}

// What a bill line whose cost was refused leaves on its line, imported or merged.
const billImportNote = (reason: string) => `[Vendor bill import] ${reason} — cost/quote price NOT imported, please review manually`;

// D171.1: the draft's lines as a picked bill is paired with them (server vendorBillLinesForImport), by position.
const draftLinesForPairing = (items: ClientInvoiceItemDto[]): DraftLineForPairing[] =>
  items.map((it, position) => ({
    position,
    itemName: it.itemName || '',
    itemDescription: it.itemDescription || '',
    unit: billedUnitOf(it),
    notSuppliedReason: it.notSuppliedReason || '',
    poMatches: (it.poMatches || []).flatMap((pm) => (pm.poId ? [{ poId: pm.poId, poLineNumber: pm.poLineNumber ?? null }] : [])),
  }));

// D171.1: a bill line sent onto a draft line counted in another unit: nothing of it merges there.
const billUnitsDiffer = (bill: VendorBillForImport, lineIndex: number, target: number | null) =>
  target != null && bill.pairs[lineIndex].unitsDiffer.includes(target);
const importableBillLines = (bill: VendorBillForImport, targets: Array<number | null>) =>
  bill.pairs.flatMap((_, i) => (billUnitsDiffer(bill, i, targets[i] ?? null) ? [] : [i]));

const unpickedSupplierMessage = (lines: number[]) =>
  `Line ${lines.join(', ')}: pick the supplier from the vendor list (it is marked "not in vendor list"). Nothing was saved.`;

/**
 * D86: a vendor bill supplier, as a picked vendor: its registered id (vendorId, else the
 * reconciliation matched vendor) with the printed name, or BLANK when the bill has no vendor
 * record yet - never a guessed name; the person picks one.
 */
export function billSupplier(
  vendorInvoice: Pick<VendorBillForImport, 'vendorId' | 'vendorReconciliation' | 'extracted'> | null | undefined
): { supplierName: string; supplierVendorId: string | null } {
  const id = vendorInvoice?.vendorId || vendorInvoice?.vendorReconciliation?.matchedVendorId || null;
  return id
    ? { supplierName: String(vendorInvoice?.vendorReconciliation?.matchedVendorName || vendorInvoice?.extracted?.vendor?.name || ''), supplierVendorId: String(id) }
    : { supplierName: '', supplierVendorId: null };
}

function buildBlankLineItem(lineNumber: number): ClientInvoiceItemDto {
  return buildDraftItem({
    lineNumber,
    confirmedOrderItemIndex: null,
    sourceType: 'manual',
    supplierName: '',
    itemName: '',
    itemDescription: '',
    confirmedQuantity: 0,
    invoiceQuantity: 0,
    billQuantity: 0,
    billUnit: '',
    unit: '',
    quoteUnitPrice: null,
    actualUnitCost: null,
    poMatches: [],
    discrepancyAlert: '',
    packingQuantity: 0,
    packingUnit: '',
    dispatchedQuantity: 0,
    dispatchQuantity: 0,
    // Q2 — blank lines are flagged as manual additions (not on the original CO)
    isManualAddition: true,
  } as any);
}

// Every cost a person sets says how, on every line (D157.9): 'manual' typed, 'chosen' picked on the line (a suggestion,
// a vendor bill, a unit tier). On an order line it also keeps the server from re-costing it (owner 2026-10-01).
const personCostBasis = (kind: 'manual' | 'chosen'): Partial<ClientInvoiceItemDto> => ({ costBasis: kind });

// D96.7 / TECH_DEBT #303 — what a line billed is the money it STORES. On an edit (`edit` given),
// the line is compared with its SAVED copy (`edit.saved`, the last server copy matched by line
// number - never the previous draft, so 1 -> 2 -> 1 lands back on the stored total): unchanged
// quantity, selling price and unit keep the saved money; any other change re-prices through the
// owner priceClientInvoiceLine (rate x qty, one ceil; D96.6: no selling rate is "unpriced", a stated 0
// bills 0). A line with no saved copy (a new row) is priced the same way. On load (`edit` absent)
// the stored total is kept. Mirrors keepBilledLineMoney in clientInvoice.service.js.
const billedUnitOf = (item: ClientInvoiceItemDto) => String(item.billUnit || item.unit || '').trim();

function buildDraftItem(item: ClientInvoiceItemDto, edit?: { saved?: ClientInvoiceItemDto }) {
  const confirmedQuantity = toNumber(item.confirmedQuantity, 0);
  // The owner's reading (invoiceQuantity, else totalQtyBilled, else 0) - never filled from confirmedQuantity.
  const qty = billedQuantity(item) ?? 0;
  const billQuantity = toNumber(item.billQuantity ?? qty, qty);
  const quoteUnitPrice = item.quoteUnitPrice == null ? null : toNumber(item.quoteUnitPrice, 0);
  const quoteLineTotal =
    item.quoteLineTotal == null
      ? quoteUnitPrice == null
        ? null
        : Number((quoteUnitPrice * confirmedQuantity).toFixed(2))
      : toNumber(item.quoteLineTotal, 0);
  const actualUnitCost = item.actualUnitCost == null ? null : toNumber(item.actualUnitCost, 0);
  const saved = edit?.saved;
  const unchanged =
    !!saved &&
    (billedQuantity(saved) ?? 0) === qty &&
    num(saved.quoteUnitPrice) === quoteUnitPrice &&
    billedUnitOf(saved) === billedUnitOf(item);
  // A legacy line that never stored a rate is billed at total / qty (what the server keeps pricing it at).
  const rate = quoteUnitPrice ?? (saved && num(saved.quoteUnitPrice) === null ? billedUnitPrice(saved) : null);
  const priced = priceClientInvoiceLine({ quantity: qty, quoteUnitPrice: rate, actualUnitCost, quoteLineTotal });
  const invoiceLineTotal = unchanged
    ? (saved!.invoiceLineTotal ?? null)
    : !edit && item.invoiceLineTotal != null
      ? toNumber(item.invoiceLineTotal, 0)
      : priced.invoiceLineTotal;
  const actualLineCost = priced.actualLineCost;
  const markupAmount =
    invoiceLineTotal == null || actualLineCost == null ? null : Number((invoiceLineTotal - actualLineCost).toFixed(2));
  // D119.15: the REALISED % of the money the line stores, never the typed rate (`typedMarkupPercent`). The owner
  // prices the kept total as one unit against the line cost, as the server's keptTotalMarkup does; a line billed 0
  // or less takes the owner's own % at its quantity, which the owner prices as 0 (D119.19, D153.9).
  const markupPercent =
    qty > 0
      ? invoiceLineTotal == null || actualLineCost == null
        ? null
        : priceClientInvoiceLine({ quantity: 1, quoteUnitPrice: invoiceLineTotal, actualUnitCost: actualLineCost }).markupPercent
      : priced.markupPercent;
  const varianceAmount =
    quoteLineTotal == null || actualLineCost == null ? null : Number((quoteLineTotal - actualLineCost).toFixed(2));
  const variancePercent =
    varianceAmount == null || quoteLineTotal == null || quoteLineTotal === 0
      ? null
      : Number(((varianceAmount / quoteLineTotal) * 100).toFixed(2));
  // 2026-09-22 one-owner audit — vessels are never charged VAT
  // (BUSINESS_RULES#client-invoice-line-vat). This is the one function every
  // client-invoice line — loaded from the server, built from a vendor bill, a
  // suggestion or a blank row, or edited on screen — passes through, so
  // forcing it here is enough to keep the whole page from ever showing or
  // saving a nonzero client-invoice VAT, whatever `item` carries in.
  const vatApplied = false;
  const vatPercent = 0;
  const vatAmount = resolveVatAmount({ vatApplied, vatPercent, taxableBase: invoiceLineTotal });

  return {
    ...item,
    confirmedQuantity,
    ...(item.invoiceQuantity == null ? {} : { invoiceQuantity: toNumber(item.invoiceQuantity, 0) }),
    billQuantity,
    quoteUnitPrice,
    quoteLineTotal,
    actualUnitCost,
    actualLineCost,
    combinedUnitCost: actualUnitCost ?? num(item.combinedUnitCost),
    markupPercent,
    typedMarkupPercent: num(item.typedMarkupPercent),
    markupAmount,
    invoiceLineTotal,
    varianceAmount,
    variancePercent,
    vatApplied,
    vatPercent,
    vatAmount,
    packingQuantity: toNumber(item.packingQuantity ?? qty, qty),
    packingUnit: item.packingUnit || item.billUnit || item.unit || '',
    dispatchedQuantity: toNumber(item.dispatchedQuantity, 0),
    dispatchQuantity: toNumber(item.dispatchQuantity, 0),
    inventoryItemId: String((item as any).inventoryItemId || ''),
  };
}

const CURRENCY_PICK_PROMPT = 'Pick the invoice currency (TTD / USD)';

const INVOICE_CHANGED_MESSAGE =
  'This invoice changed after you opened it (for example, the order\'s quantities changed). ' +
  'Nothing was saved. Reload the invoice and make your edit again.';

// The slip path: its save has already gone through when the slip POST finds the invoice changed.
const SLIP_INVOICE_CHANGED_MESSAGE =
  'The delivery slip was not made: this invoice changed after you opened it. ' +
  'Your other edits were saved. Reload the invoice and stage the slip again.';
// Failure text by machine code (invariant 6: never the server prose). Unknown codes get the caller generic text.
const SAVE_FAILURE_BY_CODE: Record<string, string> = {
  CLIENT_INVOICE_CHARGES_LOCKED: "This invoice has already been sent — its charges are locked and cannot be edited. Raise a credit note to adjust what the client owes. Nothing was changed.",
  FX_RATE_NOT_SET: "This invoice has no TT$/US$ rate pinned — the currency cannot be converted. Nothing was changed.",
  CLIENT_INVOICE_CURRENCY_UNCHANGED: "The invoice is already in that currency. Nothing was changed.",
  CLIENT_INVOICE_CURRENCY_NEEDS_CONVERSION: "This invoice has prices, so changing its currency has to convert them. Use the currency control above the worksheet. Nothing was saved.",
  CLIENT_INVOICE_CURRENCY_REQUIRED: "Pick the invoice currency (TTD / USD) before saving. Nothing was saved.",
  CLIENT_INVOICE_CHARGES_NOT_ON_ADDITIONAL: "This is an ADDITIONAL invoice — it bills at full price with no discount and no charges. Nothing was changed.",
  // D96 (owner 2026-10-06) — status flow, locks, currency.
  CLIENT_INVOICE_STATUS_TRANSITION_INVALID: "This invoice cannot move to that status from where it is. Nothing was changed.",
  CLIENT_INVOICE_LINES_LOCKED: "This invoice has been sent — its lines are locked. Raise a credit note to change what the client owes. Nothing was changed.",
  CLIENT_INVOICE_CANCELLED: "This invoice is cancelled, so it cannot be changed. Nothing was changed.",
  DELIVERY_SLIP_INVOICE_SENT: "This invoice has been sent, so no new slip can be made.", // D153.11
  CLIENT_INVOICE_PARENT_CANCELLED: "The parent invoice is cancelled, so no additional invoice can be raised against it.",
  // D157.17 — a customs invoice.
  CLIENT_INVOICE_CUSTOMS_ALREADY_CHARGED: "This order already bills a customs fee, as a charge on its invoice or as custom expenses built into the quoted prices, so a customs invoice would bill it twice. Nothing was created.",
  CLIENT_INVOICE_CUSTOMS_AMOUNT_INVALID: "Type the customs fee as an amount above zero. Nothing was created.",
  CLIENT_INVOICE_STATUS_INVALID: "That is not a valid status. Nothing was changed.",
  CLIENT_INVOICE_CURRENCY_INVALID: "That is not a valid currency. Nothing was changed.",
  // D153.6 — refused on a move to sent and on the tax-invoice PDF.
  CLIENT_INVOICE_PAYER_MISSING: "Fill in the payer's name and address on the Details tab before sending or printing this invoice.",
};
// D96 refusals whose text names lines from `err.details` (the facts, never the prose).
const refusedLines = (details: any): string => {
  const lines: any[] = Array.isArray(details?.lines) ? details.lines : [];
  return lines
    .map((l) => `line ${l?.lineNumber ?? '?'} ${l?.itemName || ''}${l?.dispatched != null ? ` — dispatched ${l.dispatched}` : ''}`.trim())
    .join('; ');
};
const REFUSAL_DETAIL_TEXT: Record<string, (details: any) => string> = {
  CLIENT_INVOICE_CANCEL_DISPATCHED: (d) =>
    `This invoice cannot be cancelled: goods have already been shipped (${refusedLines(d) || 'some lines'}). Raise a credit note to return them. Nothing was changed.`,
  CLIENT_INVOICE_LINE_UNPRICED: (d) =>
    `Every line needs a selling price before the invoice can be marked Sent. Unpriced: ${refusedLines(d) || 'some lines'}. Nothing was changed.`,
  CLIENT_INVOICE_LINE_REMOVAL_LOCKED: (d) =>
    `line ${d?.lineNumber ?? '?'} ${d?.itemName || ''} cannot be removed: it has been ${d?.reason === 'credited' ? 'credited' : 'dispatched'}. Raise a credit note instead. Nothing was changed.`,
  // D170.7: an order line is kept as Not supplied, and bills or ships nothing until restored.
  CLIENT_INVOICE_ORDER_LINE_KEPT: (d) =>
    `Line ${d?.lineNumber ?? '?'}${d?.itemName ? ` ${d.itemName}` : ''} came from the confirmed order, so it stays on the invoice: Remove marks it Not supplied. Reload the invoice and try again. Nothing was changed.`,
  CLIENT_INVOICE_LINE_NOT_SUPPLIED: (d) =>
    `Line ${d?.lineNumber ?? '?'}${d?.itemName ? ` ${d.itemName}` : ''} is marked Not supplied, so it cannot be billed or put on a slip. Restore the line in its side panel first. Nothing was changed.`,
  // D157.6: billed never goes below what the slips shipped (save, writeback, the slip's own save).
  CLIENT_INVOICE_BILLED_BELOW_SHIPPED: (d) =>
    `Line ${d?.lineNumber ?? '?'}${d?.itemName ? ` ${d.itemName}` : ''} cannot be billed ${d?.billedQuantity ?? '?'}: its delivery slips already shipped ${d?.dispatchedQuantity ?? '?'}. To bill less than was shipped, raise a credit note. Nothing was changed.`,
  // D172.10: billed never goes below what the line's credit notes credit (save, writeback).
  CLIENT_INVOICE_BILLED_BELOW_CREDITED: (d) =>
    `Line ${d?.lineNumber ?? '?'}${d?.itemName ? ` ${d.itemName}` : ''} would bill ${d?.billedQuantity ?? '?'}, below the ${d?.creditedQuantity ?? '?'} its credit notes already credit. Cancel or change the credit note first. Nothing was changed.`,
  // D157.6: a line billed 0 with no rate cannot take a new quantity (a slip's extra, a writeback pick).
  CLIENT_INVOICE_LINE_HAS_NO_RATE: (d) =>
    `Line ${d?.lineNumber ?? '?'}${d?.itemName ? ` ${d.itemName}` : ''} has no price to bill a new quantity at. Enter its selling price, then try again.`,
  // D153.9: the tax-invoice PDF refuses a stored Subtotal its printed lines do not add up to.
  INVOICE_PDF_TOTALS_DISAGREE: (d) => {
    const named = refusedLines(d);
    const many = (d?.lines?.length ?? 0) > 1;
    return `The printed lines do not add up to the invoice Subtotal${named ? `: ${named} ${many ? 'are' : 'is'} billed 0 but still ${many ? 'carry' : 'carries'} a total` : ''}. Fix the line${many ? 's' : ''}, save, then print.`;
  },
};
/** Words for a refusal code (null when we have none); `details` carries the facts for list-style refusals. */
const refusalText = (code: unknown, details?: unknown): string | null =>
  code === "CLIENT_INVOICE_CHANGED"
    ? INVOICE_CHANGED_MESSAGE
    : REFUSAL_DETAIL_TEXT[String(code)]?.(details) ?? SAVE_FAILURE_BY_CODE[String(code)] ?? null;
const saveFailureMessage = (code: unknown, generic: string, details?: unknown) => refusalText(code, details) || generic;


// D157.6: one sentence per line whose billed quantity the slip raised, the server's copy before it against after.
function billedRiseNotes(before: ClientInvoiceDto, after: ClientInvoiceDto): string[] {
  const lineOf = (item: any, index: number) => item.lineNumber ?? index + 1;
  const was = new Map((before.items || []).map((item, index) => [lineOf(item, index), billedQuantity(item)]));
  return (after.items || []).flatMap((item, index) => {
    const from = was.get(lineOf(item, index));
    const to = billedQuantity(item);
    return from != null && to != null && to > from ? [DELIVERY_FIELD_NOTES.billedRose(item.itemName || `line ${lineOf(item, index)}`, from, to)] : [];
  });
}

/** F34: stamp each line with the dispatch quantity `server` stated for it (default: the dto itself). */
function withSavedDispatch(dto: ClientInvoiceDto, server: ClientInvoiceDto = dto): ClientInvoiceDto {
  return {
    ...dto,
    items: (dto.items || []).map((item, index) => ({
      ...item,
      savedDispatchQuantity: toNumber(server.items?.[index]?.dispatchQuantity, 0),
    })),
  };
}

// D98.14 - a dateless invoice opens with the user's today (the browser's calendar day, never the UTC one, which is
// tomorrow from 20:00 in Trinidad), so what is on screen is what Save stores. Applied once per adopted server copy.
function withEffectiveInvoiceDate(dto: ClientInvoiceDto): ClientInvoiceDto {
  return { ...dto, invoiceDate: dto.invoiceDate || dto.invoiceSection?.invoiceDate || browserToday() };
}

function buildDraftFromInvoice(invoice: ClientInvoiceDto) {
  // Not `.map(buildDraftItem)`: map's index would arrive as `was` and read every line after the first as edited.
  const items = (invoice.items || []).map((item) => buildDraftItem(item));
  const deliverySlipSectionInput = invoice.deliverySlipSection || {};
  // The totals, margin, discount, charges and counts come from the one owner
  // (utils/clientInvoiceSummary.ts, twin of the server's), so the preview after every edit
  // cannot disagree with what the save stores. The invoice's discountPercentage/charges are
  // its OWN stored fields (copied once at generation, never re-derived here). A
  // zero-line invoice keeps the total typed on its header (D96.5).
  // The typed total is the SAVED invoice's, and only when that had no lines (D96.5): taken once, from
  // the server copy, then carried on the draft - never re-read from the draft's own summary, which
  // after "remove every line" still holds the removed lines' money.
  const savedTotal = num(invoice.summary?.quotedTotal);
  const typedHeaderTotal: number | null =
    'typedHeaderTotal' in invoice
      ? (invoice as any).typedHeaderTotal
      : (invoice.items || []).length === 0 && savedTotal !== null && savedTotal >= 0 ? savedTotal : null;
  const owned = buildClientInvoiceSummary(items, {
    discountPercent: invoice.discountPercentage,
    charges: invoice.charges,
    headerTotal: typedHeaderTotal,
  });
  // "Apply returns" is retired (D44 #7, #45) and nothing on this page stages a return any more,
  // so the count is no longer derived here. It is carried through as stored because the save sends
  // the WHOLE summary, and a key left out would be reset to the schema default rather than kept.
  const returnCount = toNumber(invoice.summary?.returnCount, 0);
  const { quotedTotal, actualCostTotal, markupValueTotal, profitVariance, profitMarginPct, discrepancyCount } = owned;
  const deliverySlipSlips = Array.isArray(deliverySlipSectionInput.slips) ? deliverySlipSectionInput.slips : [];
  const deliverySlipCount = deliverySlipSlips.length;
  const dispatchedQuantityTotal = items.reduce((sum, item) => sum + toNumber(item.dispatchedQuantity, 0), 0);
  const pendingDispatchQuantityTotal = items.reduce((sum, item) => sum + remainingDispatchQty(item), 0);

  // D98.14 - ONE date: the top-level one (header, Details, print and Save all read it); the section copy mirrors it.
  // Its load-time default is set once, by withEffectiveInvoiceDate; an edit rebuilds the draft from it as-is, so a
  // date the user clears stays cleared (and Save refuses it) instead of the section copy or today filling it back.
  const effectiveInvoiceDate = invoice.invoiceDate ?? '';
  return {
    ...invoice,
    invoiceDate: effectiveInvoiceDate,
    typedHeaderTotal,
    items,
    summary: {
      ...owned,
      returnCount,
      deliverySlipCount,
      dispatchedQuantityTotal: Number(dispatchedQuantityTotal.toFixed(2)),
      pendingDispatchQuantityTotal: Number(pendingDispatchQuantityTotal.toFixed(2)),
    },
    // The header block writes the top-level date and vessel and the server PDFs
    // read them, so they win over the section copy: one value, saved as shown.
    invoiceSection: {
      invoiceDate: effectiveInvoiceDate,
      dueDate: invoice.invoiceSection?.dueDate || invoice.dueDate || addDays(new Date().toISOString(), 30),
      clientName: invoice.invoiceSection?.clientName || invoice.clientName || '',
      clientEmail: invoice.invoiceSection?.clientEmail || invoice.clientEmail || '',
      vesselName: invoice.vesselName ?? invoice.invoiceSection?.vesselName ?? '',
      currency: invoice.invoiceSection?.currency || invoice.currency || '',
      notes: invoice.invoiceSection?.notes || invoice.notes || '',
      poReference: invoice.invoiceSection?.poReference || invoice.quotationNumber || invoice.rfqNumber || '',
      discrepancyCount,
    },
    markupSection: {
      ...(invoice.markupSection || {}),
      quotedTotal: Number(quotedTotal.toFixed(2)),
      actualCostTotal: Number(actualCostTotal.toFixed(2)),
      markupValueTotal: Number(markupValueTotal.toFixed(2)),
      profitVariance,
      profitMarginPct,
      combinedUnitCostTotal: Number(actualCostTotal.toFixed(2)),
    },
    deliverySlipSection: {
      ...deliverySlipSectionInput,
      shipTo: deliverySlipSectionInput.shipTo || invoice.vesselName || invoice.clientName || '',
      packingList: items.map((item) => ({
        lineNumber: item.lineNumber,
        itemName: item.itemName,
        itemDescription: item.itemDescription,
        quantity: item.packingQuantity || item.invoiceQuantity || item.confirmedQuantity || 0,
        unit: item.packingUnit || item.unit || '',
      })),
      slips: deliverySlipSlips,
      // Not shown any more, but it MUST keep travelling: the save replaces the
      // whole deliverySlipSection, so dropping this would wipe the old return-log
      // entries, which the server's exposure report still reads.
      returnedItems: deliverySlipSectionInput.returnedItems || [],
    },
  } as ClientInvoiceDto;
}

export const ClientInvoicePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const columnWidths = useColumnWidths('client-invoice-lines');
  const { canCreate: canCreateInvoice, canEdit: canEditInvoice } = usePermission('invoices');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoiceDateValid, setInvoiceDateValid] = useState(true);
  const [tab, setTab] = useState<TabKey>('invoice');
  // D98: a new tab opens at its top, and the pane has bottom padding so the floating chat button
  // never covers the last column.
  const paneRef = useRef<HTMLDivElement>(null);
  // The line drawer starts below the sticky header, so Save / Mark Sent / More stay clickable.
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerBottom, setHeaderBottom] = useState<number | undefined>(undefined);
  useEffect(() => {
    if (paneRef.current) paneRef.current.scrollTop = 0;
  }, [tab]);
  const [confirmedOrder, setConfirmedOrder] = useState<any>(null);
  const [invoice, setInvoice] = useState<ClientInvoiceDto | null>(null);
  // D119.11: undo / redo of the unsaved draft. Every USER edit goes through `editInvoice`; a server copy
  // adopted on load / Save / refresh is not an edit, it starts a fresh history (adoptServerInvoice).
  // Which line the drawer shows (null = closed). The undo SHORTCUT works only while it is open on the Lines tab;
  // the history itself records every edit on the draft, wherever it is made.
  const [selectedLine, setSelectedLine] = useState<number | null>(null);
  const history = useDraftHistory<ClientInvoiceDto>(invoice, setInvoice, { enabled: tab === 'invoice' && selectedLine != null });
  const editInvoice = history.edit;
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const measure = () => setHeaderBottom(el.getBoundingClientRect().bottom);
    measure();
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(el);
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  // The header mounts only once loading ends (an order-raised invoice still awaits its order after adopting the invoice).
  }, [!!invoice && !loading]);
  // The line as it is right now, for async work that must not act on a stale render's closure.
  const invoiceRef = useRef<ClientInvoiceDto | null>(null);
  invoiceRef.current = invoice;
  // D119.11: where `line` is NOW. An index captured before an await goes stale (Undo can bring a removed line
  // back and shift every later one), so a handler that awaits then writes re-finds its line by line number (D96.9:
  // stable once given). -1: the line is gone. A line with no number (legacy) is found only at the index it had.
  const lineIndexOf = (line: ClientInvoiceItemDto, wasAt: number) => {
    const items = invoiceRef.current?.items ?? [];
    const no = num(line.lineNumber);
    if (no === null) return items[wasAt] && num(items[wasAt].lineNumber) === null ? wasAt : -1;
    return items.findIndex((it) => num(it.lineNumber) === no);
  };
  // D119.11: the newest unit change per line (by line number), so an older lookup that answers late writes nothing.
  const uomEditRef = useRef<Record<string, number>>({});
  // 2026-09-22 audit (defect #4) — the Settings-configured TT$/US$ rate
  // (BUSINESS_RULES#fx-rate-ttd-usd), loaded once: the D119.9 ladder's last rung
  // when neither the line nor the invoice is pinned. Null (never a guessed
  // fallback — CLAUDE.md forbids it) until it loads, and again if it fails.
  const [ttdToUsdRate, setTtdToUsdRate] = useState<number | null>(null);
  // D119.9: the rate THIS invoice is pinned at (TT$ per US$1; null = none), as the server reads it - the same
  // reader the procurement worksheet and the margin report use. `key` says which invoice state it answers for.
  const [invoicePinned, setInvoicePinned] = useState<{ key: string; rate: number | null } | null>(null);
  const pinnedKey = invoice?._id ? `${invoice._id}|${invoice.currencyConversion?.rate ?? ''}|${invoice.quotationId ?? ''}` : '';

  // Chain rail sources. Both of these store their id under a domain-specific
  // key (`poId`, `vendorInvoiceId`) rather than `_id`, so they are reshaped
  // into the reference form ChainRail resolves. Flattened across lines and
  // then de-duplicated by the rail: `poMatches` is per line, so one purchase
  // order covering four lines arrives four times.
  const chainPurchaseOrders = useMemo(
    () =>
      (invoice?.items || []).flatMap((item: any) =>
        (item?.poMatches || []).map((match: any) => ({
          _id: match?.poId,
          poNumber: match?.poNumber,
        }))
      ),
    [invoice]
  );

  const chainVendorInvoices = useMemo(
    () =>
      (invoice?.items || []).flatMap((item: any) =>
        (item?.sourceProvenance || []).flatMap((source: any) =>
          (source?.vendorInvoices || []).map((vendorInvoice: any) => ({
            _id: vendorInvoice?.vendorInvoiceId,
            invoiceNumber: vendorInvoice?.invoiceNumber,
          }))
        )
      ),
    [invoice]
  );
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  // Item 18 (9 Oct QA retest): how much of each line a SHORT-DELIVERY credit note already
  // took off "remaining to dispatch" — the one read, reused everywhere this page computes it
  // (the CSV export, the line drawer; DeliveryTab reads the same credit notes itself).
  const creditedShortByLine = useMemo(() => shortDeliveryCreditedQtyByLine(creditNotes), [creditNotes]);
  const [creditTotal, setCreditTotal] = useState(0); // negative sum of credit notes
  const [rowSuggestionMap, setRowSuggestionMap] = useState<Record<number, LineItemSuggestion[]>>({});
  const [rowSuggestionLoadingMap, setRowSuggestionLoadingMap] = useState<Record<number, boolean>>({});
  const [activeRowSuggestionIndex, setActiveRowSuggestionIndex] = useState<number | null>(null);
  // D157.2b Import Vendor Bill: the server's rows (order's bills first); a failed load is its message, with Retry.
  const [vendorBillSearch, setVendorBillSearch] = useState('');
  const [vendorBillResults, setVendorBillResults] = useState<CandidateInvoice[]>([]);
  const [vendorBillError, setVendorBillError] = useState<string | null>(null);
  const [vendorBillAttempt, setVendorBillAttempt] = useState(0);
  const [showVendorBillModal, setShowVendorBillModal] = useState(false);
  // The picked row, and its lines once loaded (`bill`) or why they were not (`error`).
  const [selectedVendorBill, setSelectedVendorBill] = useState<{ row: CandidateInvoice; bill: VendorBillForImport | null; error: string | null } | null>(null);
  const pickedBillId = useRef<string | null>(null);
  const pickedBill = selectedVendorBill?.bill ?? null;
  const [selectedVendorBillLines, setSelectedVendorBillLines] = useState<number[]>([]);
  // D171.1: per bill line, the draft line it goes to (its position; null: a new line), the server's pairing until changed.
  const [billLineTargets, setBillLineTargets] = useState<Array<number | null>>([]);
  const [vendorBillLoading, setVendorBillLoading] = useState(false);
  const [creatingSlip, setCreatingSlip] = useState(false);
  const rowSuggestionTimersRef = useRef<Record<number, number>>({});
  // F34: every line carries the dispatch quantity the SERVER last stated for it
  // (`savedDispatchQuantity`, stamped on load, refresh and any save). It rides on the
  // line itself, not in a table keyed by position, because removing a row shifts the others
  // (line numbers stay, D96.9). A line added on screen has none, and nothing saved means 0.
  const [savedInvoice, setSavedInvoice] = useState<ClientInvoiceDto | null>(null);
  // The last server copy of a line (matched by line number): what an edit is judged against.
  const savedLineFor = (item: ClientInvoiceItemDto) =>
    num(item.lineNumber) === null ? undefined : (savedInvoice?.items || []).find((s) => num(s.lineNumber) === num(item.lineNumber));
  // D171.4: the supplier's billed figure is not offered on a line a credit note names (it would come off twice), nor once
  // the unit moved from the saved line's (the figure is in that unit). The drawer's "Bill X" and the Short by column both read it.
  const hidesSupplierBilled = (item: ClientInvoiceItemDto, index: number): boolean => {
    const savedLine = savedLineFor(item);
    return lineCredited(item, index, creditNotes) || (!!savedLine && billedUnitOf(savedLine) !== billedUnitOf(item));
  };
  // D183.6: every server copy the page takes also re-asks the live progress (the order changed with it). The
  // background re-read passes refetch:false because its tick asks for the progress itself.
  const adoptServerInvoice = (dto: ClientInvoiceDto, opts?: { refetch?: boolean }) => {
    setSavedInvoice(dto);
    setInvoice(buildDraftFromInvoice(withEffectiveInvoiceDate(withSavedDispatch(dto))));
    history.clear();
    markInvoiceFresh();
    if (opts?.refetch !== false) void refetchLive(dto._id);
  };
  // D98.6 - unsaved changes: the screen's draft against the last server copy it adopted. One
  // comparison (the draft is rebuilt from the server copy on every adopt, so equal means untouched)
  // instead of a flag every edit handler must remember to set.
  const isDirty = useMemo(
    () => !!savedInvoice && !!invoice && JSON.stringify(buildDraftFromInvoice(withEffectiveInvoiceDate(withSavedDispatch(savedInvoice)))) !== JSON.stringify(invoice),
    [savedInvoice, invoice]
  );
  // Closing or reloading the tab: the browser asks. (App uses BrowserRouter, not a data router, so
  // useBlocker is unavailable; in-app leaving is guarded at the header back link.)
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);
  const askDiscard = () =>
    confirm({
      title: 'Discard unsaved changes?',
      summary: 'Changes you have not saved will be lost.',
      tone: 'error',
      confirmLabel: 'Discard',
    });
  // D98.6: every exit this page owns (back link, More > Raise credit note, the chain rail, a Details credit-note row) asks before it drops unsaved edits.
  const guardLeave = async (e: React.MouseEvent, to: string) => {
    if (!isDirty) return;
    e.preventDefault();
    if (await askDiscard()) navigate(to);
  };
  const [undispatchedPrompt, setUndispatchedPrompt] = useState<{ undispatched: number | null; total: number | null } | null>(null);
  const [feedbackModal, setFeedbackModal] = useState<FeedbackModalState>({
    open: false,
    tone: 'success',
    title: '',
    message: '',
  });

  // D96.14: no silent default - '' means "not chosen yet" and the page asks for one.
  // The invoice's OWN currency only (top-level first): the confirmed order's schema default is 'USD'
  // and borrowing it showed a currency the invoice never had, so Save was refused for a "missing" one.
  const currency = invoiceCurrencyOf(invoice);
  // Who and which vessel, read ONE way everywhere on this page: the invoice's own (copied from the order
  // when it is built, and editable here), else the order's. Header, export, slip print and delivery cards agree.
  // The name inside an email's From header, never the header itself (orderIdentity owns how a client is labelled).
  const partyClient = clientDisplayName(invoice?.clientName || confirmedOrder?.confirmedOrder?.clientName) || '--';
  const partyVessel = invoice?.vesselName || confirmedOrder?.confirmedOrder?.vesselName || '--';
  // What the SERVER holds. Convert-vs-relabel is decided from this, never from the draft.
  const savedCurrency = invoiceCurrencyOf(savedInvoice);
  const [currencyPrompt, setCurrencyPrompt] = useState<'TTD' | 'USD' | null>(null);
  const currencyLocked = !!invoice && (isInvoiceSentOnward(invoice.status) || invoice.status === 'cancelled');
  const setDraftCurrency = (next: string) =>
    editInvoice((prev) =>
      prev ? { ...prev, currency: next, invoiceSection: { ...(prev.invoiceSection || {}), currency: next } } : prev
    );
  const onCurrencyPick = (next: string) => {
    if (next !== 'TTD' && next !== 'USD') return;
    if (next === currency) return;
    // Converting needs a currency to convert FROM: the first pick is just a choice.
    if (savedCurrency && invoiceHasMoney(savedInvoice)) setCurrencyPrompt(next);
    else setDraftCurrency(next);
  };
  const confirmCurrencyConversion = async () => {
    const to = currencyPrompt;
    setCurrencyPrompt(null);
    if (!to || !invoice) return;
    // Converting adopts the stored invoice, which would silently drop what was typed since the
    // last save: refuse until the draft matches it.
    if (isDirty) {
      openFeedbackModal('error', 'Convert currency failed', 'Save your changes first, then change the currency. Nothing was changed.');
      return;
    }
    await runAction('Convert currency', async () => {
      adoptServerInvoice(await convertClientInvoiceCurrency(invoice._id, { currency: to, expectedUpdatedAt: invoice.updatedAt }));
    });
  };

  // Entered by a confirmed-order id. Creating is a write (the invoice counter
  // moves): only a user who can create goes through the create-or-get POST; the
  // rest read the order's invoice, which is a 404 until someone creates it.
  const invoiceForOrder = (orderId: string) =>
    canCreateInvoice
      ? createOrGetClientInvoiceFromConfirmedOrder(orderId)
      : fetchClientInvoiceByConfirmedOrderId(orderId);

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      try {
        const invoiceRes = await fetchClientInvoiceById(id);
        adoptServerInvoice(invoiceRes);
        if (invoiceRes.confirmedOrderId) {
          const confirmedRes = await fetchConfirmedOrderById(invoiceRes.confirmedOrderId);
          setConfirmedOrder(confirmedRes);
        }
      } catch (_) {
        const [confirmedRes, invoiceRes] = await Promise.all([
          fetchConfirmedOrderById(id),
          invoiceForOrder(id),
        ]);
        setConfirmedOrder(confirmedRes);
        adoptServerInvoice(invoiceRes);
        // Entered by the order's id (Create invoice): the page now moves to the invoice's own address, which is
        // the one the sidebar's trail reads, so the trail shows the moment the invoice exists. A replace, so Back
        // returns to the order and not to this hand-off address.
        if (invoiceRes._id) navigate(`/client-invoices/${invoiceRes._id}`, { replace: true });
      }
    } catch (e: any) {
      setError(
        e?.code === 'CLIENT_INVOICE_NOT_FOUND'
          ? 'No invoice has been created for this order yet.'
          : 'The invoice could not be loaded. Check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // 2026-09-22 audit (defect #4) — load the owner's configured rate once, the
  // same call QuotationDetailPage's loadExchangeRate makes. A failure leaves
  // it null (no guessed fallback): a line or invoice pin still applies, and a
  // cross-currency import with none refuses and says so.
  useEffect(() => {
    let cancelled = false;
    getExchangeRate()
      .then((rateData) => {
        if (cancelled) return;
        const rate = Number(rateData?.rate);
        setTtdToUsdRate(Number.isFinite(rate) && rate > 0 ? rate : null);
      })
      .catch((err) => {
        console.error('Failed to load exchange rate from settings:', err);
        if (!cancelled) setTtdToUsdRate(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // D119.9: the invoice's pinned rate, from the server's one reader. A failure leaves it unknown (the drawer's
  // TT$ cost then says so) - never a fall to Settings, which could differ from what the worksheet would use.
  useEffect(() => {
    if (!pinnedKey) return undefined;
    let cancelled = false;
    fetchInvoicePinnedRate(pinnedKey.split('|')[0])
      .then((r) => { if (!cancelled) setInvoicePinned({ key: pinnedKey, rate: r.usdToTtd }); })
      .catch((err) => console.error('Failed to load the invoice pinned rate:', err));
    return () => { cancelled = true; };
  }, [pinnedKey]);

  // CN-8: show credit notes raised against this invoice + running net.
  useEffect(() => {
    const invId = invoice?._id;
    if (!invId) {
      setCreditNotes([]);
      setCreditTotal(0);
      return;
    }
    let cancelled = false;
    getCreditNotesForInvoice(invId)
      .then((res) => {
        if (cancelled) return;
        setCreditNotes(res.creditNotes);
        setCreditTotal(res.totalCredited);
      })
      .catch(() => {
        if (!cancelled) {
          setCreditNotes([]);
          setCreditTotal(0);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [invoice?._id]);

  // D183.3: the bills still being entered (provisional, read-only), asked ONCE when the invoice is known and by the
  // INVOICE's own id (the route id may be a confirmed order's). A failure is shown, never hidden: the figures then
  // come from stored data only. D183.6: asked again after every server copy the page adopts (refetchLive in
  // adoptServerInvoice) and by refreshInBackground (focus, visible, every 60 s). Last answer wins; a failed re-ask
  // keeps the figures already shown for the same invoice and marks them stale.
  const beginLiveRequest = useLatestRequest();
  const [live, setLive] = useState<{ invoiceId: string; data: LiveProgress | null; readAt: Date | null; failedAt: Date | null } | null>(null);
  const refetchLive = (invId: string | undefined): Promise<boolean> => {
    if (!invId) return Promise.resolve(true);
    const isCurrent = beginLiveRequest();
    return fetchLiveProgress(invId)
      .then((data) => {
        if (isCurrent()) setLive({ invoiceId: invId, data, readAt: new Date(), failedAt: null });
        return true;
      })
      .catch((err) => {
        console.error('Failed to load the bills still being entered:', err);
        // Only the latest request's outcome counts; the figures already shown for this invoice are kept, with their time.
        if (isCurrent()) {
          setLive((prev) => {
            const same = prev && prev.invoiceId === invId ? prev : null;
            return { invoiceId: invId, data: same ? same.data : null, readAt: same ? same.readAt : null, failedAt: new Date() };
          });
        }
        return false;
      });
  };
  // D183.6: what the header says is DERIVED from these facts, never stored as prose. invoiceReadAt: the last time the
  // invoice copy on screen was read from or written to the server. invoiceTick: what the LATEST tick or Refresh did
  // with the invoice when it did not get a fresh copy (skipped, with the reason, or failed); one field, so a newer
  // outcome replaces an older one and the two can never contradict. The live read's own facts sit in `live`.
  const [invoiceReadAt, setInvoiceReadAt] = useState<Date | null>(null);
  const [invoiceTick, setInvoiceTick] = useState<
    { kind: 'skipped'; why: 'unsaved' | 'busy' } | { kind: 'failed'; at: Date } | null
  >(null);
  const [refreshing, setRefreshing] = useState(false);
  // A fresh copy of the invoice (load, any adopt, a save, a background read that found nothing newer).
  const markInvoiceFresh = () => { setInvoiceReadAt(new Date()); setInvoiceTick(null); };
  const beginInvoiceReread = useLatestRequest();
  // What a background read must check AFTER its await, from the latest render: a person may have started typing or
  // saved while the request was in flight.
  const latestRef = useRef({ invoiceId: undefined as string | undefined, isDirty: false, saving: false, creatingSlip: false, savedUpdatedAt: undefined as string | undefined });
  latestRef.current = { invoiceId: invoice?._id, isDirty, saving, creatingSlip, savedUpdatedAt: savedInvoice?.updatedAt };
  const mayRereadInvoice = (): boolean => {
    const l = latestRef.current;
    return !l.isDirty && !l.saving && !l.creatingSlip && !busyRef.current;
  };
  const skipReason = (): 'unsaved' | 'busy' => (latestRef.current.isDirty ? 'unsaved' : 'busy');
  // The invoice and its credit notes, silently. Never refreshFromServer / reload (they flip `saving` and open a
  // modal) and never askDiscard.
  const rereadInvoice = async (invId: string): Promise<void> => {
    const isCurrent = beginInvoiceReread();
    const startedAt = latestRef.current.savedUpdatedAt;
    try {
      const [fresh, cn] = await Promise.all([fetchClientInvoiceById(invId), getCreditNotesForInvoice(invId)]);
      // Another read, a save or another invoice took over meanwhile: this answer is out of date, drop it.
      if (!isCurrent() || latestRef.current.invoiceId !== invId || latestRef.current.savedUpdatedAt !== startedAt) return;
      // Typed (or a save / slip started) while it was in flight: keep the edit, say the invoice was not re-read.
      if (!mayRereadInvoice()) { setInvoiceTick({ kind: 'skipped', why: skipReason() }); return; }
      setCreditNotes(cn.creditNotes);
      setCreditTotal(cn.totalCredited);
      if (fresh.updatedAt !== startedAt) adoptServerInvoice(fresh, { refetch: false });
      else markInvoiceFresh();
    } catch (err) {
      console.error('Failed to re-read the invoice in the background:', err);
      if (isCurrent()) setInvoiceTick({ kind: 'failed', at: new Date() });
    }
  };
  const refreshInBackground = async (manual = false) => {
    const invId = latestRef.current.invoiceId;
    if (!invId) return;
    const mayReread = mayRereadInvoice();
    if (manual) setRefreshing(true);
    try {
      const liveRead = refetchLive(invId);
      if (mayReread) await rereadInvoice(invId);
      else setInvoiceTick({ kind: 'skipped', why: skipReason() });
      await liveRead;
    } finally {
      if (manual) setRefreshing(false);
    }
  };
  useVisiblePolling({ onTick: () => { void refreshInBackground(); }, enabled: !!invoice?._id });
  const liveAnswered = live && live.invoiceId === invoice?._id ? live : null;
  const refreshStatus: { text: string; tone: 'ok' | 'skipped' | 'failed' } = (() => {
    const invoiceAt = invoiceReadAt ? fmtDateTime(invoiceReadAt) : '';
    if (invoiceTick?.kind === 'skipped') {
      const why = invoiceTick.why === 'unsaved' ? 'unsaved changes' : 'another action is running';
      return { text: `Invoice not re-read (${why}); last read ${invoiceAt}`, tone: 'skipped' };
    }
    if (invoiceTick?.kind === 'failed') return { text: `Could not refresh at ${fmtDateTime(invoiceTick.at)}; invoice as of ${invoiceAt}`, tone: 'failed' };
    if (!invoiceReadAt) return { text: '', tone: 'ok' };
    const billsAt = liveAnswered?.readAt ? fmtDateTime(liveAnswered.readAt) : '';
    return { text: `Updated ${invoiceAt}${billsAt && billsAt !== invoiceAt ? ` · bills ${billsAt}` : ''}`, tone: 'ok' };
  })();
  // The ONE progress figure of this invoice (orderProgress), read by the header strip and the Profitability tab.
  const progress = useMemo(() => {
    const items = invoice?.items || [];
    const { rows } = buildProfitability({ items }, creditNotes, invoice?._id);
    const answered = live && live.invoiceId === invoice?._id ? live : null;
    const entered = new Map<number, EnteredCost>((answered?.data?.entered || []).map((e) => [e.lineNumber, e]));
    return orderProgress(items, rows, creditedShortByLine, entered);
  }, [invoice?.items, invoice?._id, creditNotes, creditedShortByLine, live]);

  // Loads on open (the order's bills) and on each search; the server leaves out expenses and bills with no lines.
  const invoiceId = invoice?._id;
  useEffect(() => {
    if (!showVendorBillModal || !invoiceId) return undefined;
    let cancelled = false;
    // At once: "no bills" must not show before the answer.
    setVendorBillLoading(true);
    setVendorBillError(null);
    const timer = window.setTimeout(async () => {
      try {
        const rows = await listClientInvoiceVendorBills(invoiceId, vendorBillSearch.trim());
        if (!cancelled) setVendorBillResults(rows);
      } catch (e) {
        if (!cancelled) {
          setVendorBillResults([]);
          setVendorBillError(`The vendor bills could not be loaded. ${describeApiError(e, { action: 'Load vendor bills' }).summary}`);
        }
      } finally {
        if (!cancelled) setVendorBillLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [showVendorBillModal, invoiceId, vendorBillSearch, vendorBillAttempt]);

  // I1-I4 — UOM Billed with real override cascade. On commit (field left / Enter): stash original
  // selling + UOM (I5/I6), then fetch the item's conversion factor and recompute cost, selling price
  // and markup at the new UOM tier. If we can't resolve a factor we leave prices alone and note it in
  // Internal Remarks so ops know to update manually.
  const applyUomChange = async (index: number, next: string) => {
    // The CURRENT line, read from the ref: this runs once, when the field is left, and the person may
    // have edited this line (or another) since the screen last rendered.
    const startItem = invoiceRef.current?.items[index];
    if (!startItem) return;
    if (next === (startItem.billUnit || startItem.packingUnit || startItem.unit || '')) return; // nothing to start
    // D119.11: this is now the newest unit change on this line; an older one still awaiting must not write.
    const lineKey = num(startItem.lineNumber) ?? `at${index}`;
    const myEdit = (uomEditRef.current[lineKey] = (uomEditRef.current[lineKey] ?? 0) + 1);

    // Resolve the item's UOM factor + cost tiers
    const lookedUp = startItem.itemName || startItem.itemDescription || '';
    let factor = null as any;
    try {
      factor = await resolveUomFactor(lookedUp);
    } catch {
      // A failed LOOKUP is not 'item not in inventory' (that is a successful lookup that finds
      // nothing, below). Change nothing - not the unit, the prices or the remarks - and say so.
      openFeedbackModal('error', 'Unit not changed', 'Could not look up the unit conversion - the unit and prices were not changed. Try again.');
      return;
    }

    // The line as it is NOW, found by its number (D119.11: Undo may have moved it). Gone, or a newer unit change
    // on this line has started since (the newest edit wins, whichever lookup answers first): write nothing.
    // Anything else changed meanwhile is kept. The write reads EVERYTHING from this re-found line (the unit it is
    // in, its price, whether it is already overridden): Undo / Redo swap whole drafts, so none of that can be
    // carried across the await. A line that no longer names the product looked up (its number reused by a new
    // line, or renamed) is not who the answer is about: write nothing.
    const refind = () => {
      if (uomEditRef.current[lineKey] !== myEdit) return null;
      const at = lineIndexOf(startItem, index);
      const now = at >= 0 ? invoiceRef.current?.items[at] : undefined;
      if (!now || (now.itemName || now.itemDescription || '') !== lookedUp) return null;
      return { at, now };
    };
    let found = refind();
    if (!found) return;

    // Map user-entered UOM string to a level key we know about.
    // Case-insensitive fuzzy match on the item's own unit names.
    const nextLower = String(next).toLowerCase().trim();
    const lvl2Lower = String(factor?.level2Name || '').toLowerCase().trim();
    const lvl1Lower = String(factor?.level1Name || '').toLowerCase().trim();
    let targetTier: 'level2' | 'level1' | 'kg' | 'lb' | null = null;
    if (factor) {
      if (nextLower === lvl2Lower) targetTier = 'level2';
      else if (nextLower === lvl1Lower) targetTier = 'level1';
      else if (nextLower === 'kg') targetTier = 'kg';
      else if (nextLower === 'lb') targetTier = 'lb';
    }

    // I6 — nonsense-unit blocker. If the item has known cost tiers
    // but the user typed a UOM that doesn't match any (e.g. "pcs"
    // when the item only sells in cases/pack/kg), the cascade will
    // silently no-op and leave stale prices. Ask before proceeding.
    if (factor && factor.costLevels && !targetTier) {
      const known = [factor.level2Name, factor.level1Name, 'kg', 'lb']
        .filter(Boolean).map((s: string) => `"${s}"`).join(', ');
      const proceed = await confirm({
        title: 'Proceed with this unit anyway?',
        summary: `The UOM "${next}" does not match any known unit for this item (known: ${known}). Cost/selling price will NOT recalculate and may be stale.`,
        confirmLabel: 'Proceed anyway',
      });
      if (!proceed) return;
      found = refind(); // the question can stay open a while
      if (!found) return;
    }
    const { at, now: item } = found;
    const prev = item.billUnit || item.packingUnit || item.unit || '';
    if (next === prev) return;
    const prevSelling = toNumber((item as any).unitPrice ?? item.quoteUnitPrice, 0);
    const isFirstOverride = !item.uomOverridden;

    const patch: any = {
      billUnit: next,
      uomOverridden: true,
    };
    if (isFirstOverride) {
      patch.originalBillUnit = prev;
      patch.originalSellingPrice = prevSelling;
      const noteLine = `[UOM override] previous UOM: "${prev}" @ ${prevSelling}`;
      patch.internalRemarks = item.internalRemarks
        ? `${item.internalRemarks}\n${noteLine}`
        : noteLine;
    }

    // A refusal note goes after the remarks as they will be: with the "previous UOM" note when this is the first override.
    const remarksWith = (note: string) => {
      const base = patch.internalRemarks ?? item.internalRemarks;
      return base ? `${base}\n${note}` : note;
    };

    // Cascade cost + selling if we have both a resolved tier and cost tiers
    if (factor && targetTier && factor.costLevels) {
      const tierKey = targetTier === 'level2' ? 'ttPerLevel2'
                    : targetTier === 'level1' ? 'ttPerLevel1'
                    : targetTier === 'kg' ? 'ttPerKg' : 'ttPerLb';
      const usdKey = targetTier === 'level2' ? 'usdPerLevel2'
                   : targetTier === 'level1' ? 'usdPerLevel1'
                   : targetTier === 'kg' ? 'usdPerKg' : 'usdPerLb';
      const newTtCost = Number(factor.costLevels[tierKey] || 0);
      const newUsdCost = Number(factor.costLevels[usdKey] || 0);
      if (newTtCost > 0 || newUsdCost > 0) {
        patch.ttCostPerUom = newTtCost || null;
        patch.usdCostPerUom = newUsdCost || null;
        patch.fxRateAtCost = factor.fxRateAtPurchase || null;

        // 2026-09-22 one-owner audit (defect #4) — a cost goes onto the
        // line in the INVOICE's currency, converted with the rate pinned
        // on this catalogue row when the two differ (BUSINESS_RULES#fx-rate-ttd-usd
        // convention). Picking whichever of newUsdCost/newTtCost happened
        // to be non-zero (the old rule) put a TT$-only cost tier straight
        // onto a US$ invoice unconverted, ~6.5x too high.
        const { cost: chosenCost, refusalReason } = resolveUomCascadeCost({
          invoiceCurrency: invoiceCurrencyOf(invoiceRef.current), // D119.11: the draft's currency NOW (a pick, or Undo, can move it)
          ttCost: newTtCost,
          usdCost: newUsdCost,
          fxRateAtPurchase: factor.fxRateAtPurchase,
        });

        if (chosenCost != null) {
          // D119.15: the new price keeps the rate the line is priced at - the typed % while it still produces the
          // price, else the ACTUAL % (lineMarkup, the owner). A line the owner cannot judge (unpriced, no cost)
          // keeps a bare typed rate, else stays unpriced: a missing rate is never 0% (sells-at-cost).
          const rate = lineMarkup(item)?.percent ?? num(item.typedMarkupPercent);
          // D119.10 (D85): the line keeps the EXACT cost and is priced once by the owner (rounded up). Markup 0
          // sells at cost, rounded up; a negative markup prices below cost, as typing it in Mark-up % does.
          // D96.6: a STATED 0 selling price is free of charge and stays 0.00 on the new unit (its realised markup
          // is -100%, which the owner clamps to -99% - it would bill 1% of cost). Unpriced (null) is not 0.
          const newSelling = num(item.quoteUnitPrice) === 0 ? 0 : rate == null ? null : sellingPriceAt(chosenCost, rate);
          patch.actualUnitCost = chosenCost;
          patch.combinedUnitCost = chosenCost;
          Object.assign(patch, personCostBasis('chosen')); // a catalogue tier, not typed
          if (newSelling != null) {
            patch.unitPrice = newSelling;
            patch.quoteUnitPrice = newSelling;
          }
        } else {
          // No rate available to state this line's cost in the invoice's
          // currency — leave actualUnitCost/unitPrice alone rather than
          // guess, and flag it the same way the "not in inventory" case does.
          const note = `[UOM override] ${refusalReason} — cost/selling NOT recalculated, please review manually`;
          patch.internalRemarks = remarksWith(note);
        }
      }
    } else if (factor === null) {
      // Item not in inventory — flag it so ops know
      const note = '[UOM override] item not in inventory catalogue — please review cost/selling manually';
      patch.internalRemarks = remarksWith(note);
    }

    updateItem(at, patch);
  };

  // `typing`: the call comes from a per-keystroke text field, so a burst of keystrokes in that field is ONE undo
  // step (D119.11); a string names the field when one patch shape serves several (the vendor-quantity slots).
  // A click, tick or pick passes nothing and is always its own step. Committing a mark-up flags it overridden
  // first and then writes it: one commit, so those two share a key (see updateMarkup).
  const updateItem = (index: number, patch: Partial<ClientInvoiceItemDto>, typing?: boolean | string) => {
    const key = 'markupOverridden' in patch
      ? `markup:${index}`
      : typing
        ? `item:${index}:${typeof typing === 'string' ? typing : Object.keys(patch).sort().join(',')}`
        : undefined;
    editInvoice((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((item, idx) => (idx === index ? buildDraftItem({ ...item, ...patch }, { saved: savedLineFor(item) }) : item));
      return buildDraftFromInvoice({ ...prev, items });
    }, key);
  };

  // D158.1: select all / clear on the Delivery tab is ONE edit, so one undo step.
  const stageDispatchQuantities = (quantities: ReadonlyArray<{ index: number; dispatchQuantity: number }>) => {
    const byIndex = new Map(quantities.map((q) => [q.index, q.dispatchQuantity]));
    editInvoice((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((item, idx) => (byIndex.has(idx) ? buildDraftItem({ ...item, dispatchQuantity: byIndex.get(idx) }, { saved: savedLineFor(item) }) : item));
      return buildDraftFromInvoice({ ...prev, items });
    });
  };

  // Owner 2026-11-02: the Lines grid's Status header marks every shown line Done (or clears them) as ONE edit, one undo step.
  const setDoneTicks = (indexes: ReadonlyArray<number>, done: boolean) => {
    const picked = new Set(indexes);
    editInvoice((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((item, idx) => (picked.has(idx) ? buildDraftItem({ ...item, completedTick: done }, { saved: savedLineFor(item) }) : item));
      return buildDraftFromInvoice({ ...prev, items });
    });
  };

  const updateItemPricing = (index: number, patch: Partial<ClientInvoiceItemDto>) => {
    editInvoice((prev) => {
      if (!prev) return prev;
      const current = prev.items[index];
      if (!current) return prev;
      const items = prev.items.map((item, idx) => (idx === index ? buildDraftItem({ ...item, ...patch }, { saved: savedLineFor(item) }) : item));
      return buildDraftFromInvoice({ ...prev, items });
    });
  };

  // The three price cells commit their TEXT (DraftInput): blank clears the cell, junk changes nothing.
  const updateCostPrice = (index: number, text: string) => {
    const parsed = parseCellText(text);
    if (parsed.kind === 'junk') return;
    const nextValue = parsed.kind === 'blank' ? null : parsed.value;
    updateItemPricing(index, {
      actualUnitCost: nextValue,
      combinedUnitCost: nextValue,
      ...personCostBasis('manual'),
    });
  };

  // D119.9: the rate a TT$ cost converts this line at (TT$ per US$1): its own pin, else the invoice's, else Settings.
  const ttdRateFor = (item?: ClientInvoiceItemDto | null) =>
    ttdCostRate({
      lineRate: item?.fxRateAtCost,
      invoiceRate: invoicePinned?.rate,
      invoiceRateKnown: !!pinnedKey && invoicePinned?.key === pinnedKey,
      settingsTtdToUsd: ttdToUsdRate,
    });

  // D119.9: a line's cost in TT$ (2 places) for a US$ invoice, with why it cannot be stated when no rate is known.
  // The one reader: the line drawer's TT$ box and the Lines table's TT$ column both show it, so they cannot differ.
  // undefined = not a US$ invoice (a TT$ invoice's Cost is already TT$; no currency yet states nothing, D96.14).
  const ttdCostFor = (item: ClientInvoiceItemDto, costPrice: number | null) => {
    if (String(currency).trim().toUpperCase() !== 'USD') return undefined;
    const found = ttdRateFor(item);
    if (found.rate == null) return { text: '', reason: found.reason };
    const ttd = costPrice == null ? null : usdToTtd(costPrice, 1 / found.rate);
    return { text: ttd == null ? '' : ttd.toFixed(2), reason: null };
  };

  // The TT$ cost the PO gave, while a bill has changed it: struck through beside the new one in the line panel. The same
  // TT$ reading as the box beside it (a US$ invoice states TT$ at the line's rate; a TT$ invoice's cost is TT$). Null when no
  // bill stands behind the cost, the PO's cost is unknown, or the two read the same to the cent.
  const ttdCostBeforeFor = (item: ClientInvoiceItemDto, costPrice: number | null): string | null => {
    const was = num(item.poUnitCost);
    if (item.costBasis !== 'billed' || was === null) return null;
    const inUsd = String(currency).trim().toUpperCase() === 'USD';
    const before = inUsd ? ttdCostFor(item, was)?.text : was.toFixed(2);
    const now = inUsd ? ttdCostFor(item, costPrice)?.text : costPrice == null ? null : costPrice.toFixed(2);
    return before && before !== now ? before : null;
  };

  // D119.9: a cost typed in TT$ is converted (unrounded, like the procurement plan's TT$ cost) into the
  // invoice's currency and written exactly like a typed Cost: manual basis, selling untouched, markup re-derived.
  // The TT$ / US$ display copies and the rate used are pinned with it, so they state the cost they sit beside.
  const updateTtdCost = (index: number, text: string) => {
    const parsed = parseCellText(text);
    if (parsed.kind === 'junk') return;
    if (parsed.kind === 'blank') return updateCostPrice(index, '');
    const item = invoice?.items[index];
    const found = ttdRateFor(item);
    if (found.rate == null) return;
    const cost = costToUsdExact(parsed.value, 'TTD', 1 / found.rate);
    if (cost == null) return;
    updateItemPricing(index, {
      actualUnitCost: cost,
      combinedUnitCost: cost,
      ttCostPerUom: parsed.value,
      usdCostPerUom: round2(cost),
      fxRateAtCost: found.rate,
      ...personCostBasis('manual'),
    });
  };

  const updateSellingPrice = (index: number, text: string) => {
    const parsed = parseCellText(text);
    if (parsed.kind === 'junk') return;
    editInvoice((prev) => {
      if (!prev) return prev;
      const current = prev.items[index];
      if (!current) return prev;
      const selling = parsed.kind === 'blank' ? null : parsed.value;
      const items = prev.items.map((item, idx) =>
        idx === index
          ? buildDraftItem({
              ...item,
              quoteUnitPrice: selling,
            }, { saved: savedLineFor(item) })
          : item
      );
      return buildDraftFromInvoice({ ...prev, items });
    });
  };

  const updateMarkup = (index: number, text: string) => {
    const parsed = parseCellText(text);
    if (parsed.kind === 'junk') return;
    editInvoice((prev) => {
      if (!prev) return prev;
      const current = prev.items[index];
      if (!current) return prev;
      const markup = parsed.kind === 'blank' ? null : parsed.value;
      const cost = toNumber(current.actualUnitCost ?? current.combinedUnitCost, 0); // a null cost is no cost (D96.8)
      // No cost to mark up: store nothing (a % that moves no price is a lie).
      if (!Number.isFinite(cost) || cost <= 0) return prev;
      // D119.10 (D85): cost x rate rounded UP once, by the one owner - never toFixed (round-half).
      const quoteUnitPrice = markup == null ? null : sellingPriceAt(cost, markup);
      const items = prev.items.map((item, idx) =>
        idx === index
          ? buildDraftItem({
              ...item,
              typedMarkupPercent: markup, // D119.15: the typed rate (blank = none, null, never 0); markupPercent stays the realised %
              quoteUnitPrice: quoteUnitPrice ?? item.quoteUnitPrice,
            }, { saved: savedLineFor(item) })
          : item
      );
      return buildDraftFromInvoice({ ...prev, items });
    }, `markup:${index}`);
  };

  // 2026-09-22 one-owner audit — vessels are never charged VAT
  // (BUSINESS_RULES#client-invoice-line-vat). `updateVatApplied`/
  // `updateVatPercent` used to back the VAT Applied / VAT % grid cells;
  // removed with those cells (buildDraftItem forces both to false/0 on every
  // save regardless, so they had become dead controls offering an option the
  // server would silently refuse).

  // D170.7: why an order line was not supplied; asked again until a reason is typed, null when cancelled.
  const askNotSuppliedReason = async (item: ClientInvoiceItemDto, hint?: string): Promise<string | null> => {
    let typed = '';
    const kept = await confirm({
      title: 'Why was this not supplied?',
      summary: `"${item.itemName || item.itemDescription || 'This line'}" came from the order, so it stays on the invoice as Not supplied: billed 0 and off the invoice print.`,
      hint,
      body: <input aria-label="Why it was not supplied" autoFocus onChange={(e) => { typed = e.target.value; }} className="h-8 w-full rounded border border-slate-300 px-2 text-sm" />,
      confirmLabel: 'Keep as Not supplied',
    });
    if (!kept) return null;
    return typed.trim() || askNotSuppliedReason(item, 'Type why it was not supplied.');
  };

  const removeItem = async (index: number) => {
    const asked = invoiceRef.current?.items[index];
    if (!asked) return;
    // D170.7: a line from the order is never deleted: it stays as Not supplied (billed 0, no source quantity) with the reason.
    const reason = isOrderDerivedLine(asked) ? await askNotSuppliedReason(asked) : undefined;
    if (reason === null) return;
    if (reason === undefined && !(await confirm({ title: 'Remove this line from the invoice?', tone: 'error', confirmLabel: 'Remove' }))) return;
    const at = lineIndexOf(asked, index); // the line that was asked about, wherever Undo has put it (D119.11)
    if (at < 0) return;
    editInvoice((prev) => {
      if (!prev) return prev;
      // Line numbers are stable once given (D96.9): the delivery and any credit note are keyed to them.
      const items = reason === undefined
        ? prev.items.filter((_, idx) => idx !== at)
        : prev.items.map((item, idx) => (idx !== at ? item : buildDraftItem({
            ...item, invoiceQuantity: 0, totalQtyBilled: 0, billQuantity: 0, qtyFromPantry: 0, qtyFromStock: 0, dispatchQuantity: 0,
            vendorQuantities: (item.vendorQuantities || []).map((v) => ({ ...v, quantity: 0 })), notSuppliedReason: reason,
          }, { saved: savedLineFor(item) })));
      return buildDraftFromInvoice({ ...prev, items });
    });
    setRowSuggestionMap({});
    setRowSuggestionLoadingMap({});
    setActiveRowSuggestionIndex(null);
  };

  // D56 — charge-row edits. Draft-only (same pattern as updateItem/removeItem
  // above): buildDraftFromInvoice recomputes discountAmount/chargesTotal/
  // invoiceGrandTotal off the new array on every call, so the totals block
  // reflects an edit immediately, before Save. The actual persistence is the
  // page's existing PATCH path (buildInvoicePatchPayload -> saveInvoice);
  // there is no per-row auto-save, matching how every other cell on this page
  // works. The discount PERCENT has no editor here — patchClientInvoice's
  // allow-list omits it on purpose (copied once at generation).
  const updateCharge = (index: number, patch: Partial<InvoiceChargeRow>) => {
    editInvoice((prev) => {
      if (!prev) return prev;
      const charges = (prev.charges || []).map((c, idx) => (idx === index ? { ...c, ...patch } : c));
      return buildDraftFromInvoice({ ...prev, charges });
    }, 'label' in patch ? `charge:${index}:label` : undefined); // only the label is typed per keystroke; an amount commits on blur
  };

  const addCharge = () => {
    editInvoice((prev) => {
      if (!prev) return prev;
      const charges = [...(prev.charges || []), { label: '', amount: 0 }];
      return buildDraftFromInvoice({ ...prev, charges });
    });
  };

  const removeCharge = (index: number) => {
    editInvoice((prev) => {
      if (!prev) return prev;
      const charges = (prev.charges || []).filter((_, idx) => idx !== index);
      return buildDraftFromInvoice({ ...prev, charges });
    });
  };

  const clearRowSuggestions = (index: number) => {
    setRowSuggestionMap((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setRowSuggestionLoadingMap((prev) => {
      if (!prev[index]) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const searchRowSuggestions = (index: number, query: string) => {
    const trimmed = query.trim();
    if (!trimmed) {
      clearRowSuggestions(index);
      return;
    }

    const existingTimer = rowSuggestionTimersRef.current[index];
    if (existingTimer) window.clearTimeout(existingTimer);

    setRowSuggestionLoadingMap((prev) => ({ ...prev, [index]: true }));
    rowSuggestionTimersRef.current[index] = window.setTimeout(async () => {
      try {
        const [inventoryRes, masterRes, supplierBills] = await Promise.all([
          getInventoryItems({ q: trimmed, page: 1, limit: 5 }),
          searchMasterCatalogue(trimmed, 5),
          fetchSupplierDropdownDataFromAllTables(trimmed),
        ]);

        const inventoryItems = (inventoryRes.items || []).map((item: any) => normalizeLineItemSuggestion('inventory', item));
        const masterItems = (masterRes.data || []).map((item: any) => normalizeLineItemSuggestion('catalogue', item));
        const supplierItems = supplierBills.map((item) => normalizeSupplierBillSuggestion(item));
        const merged = [...inventoryItems, ...masterItems, ...supplierItems];
        const seen = new Set<string>();
        const deduped = merged.filter((item) => {
          const key = `${item.title}|${item.subtitle}|${item.price ?? ''}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setRowSuggestionMap((prev) => ({ ...prev, [index]: deduped }));
      } catch {
        setRowSuggestionMap((prev) => ({ ...prev, [index]: [] }));
      } finally {
        setRowSuggestionLoadingMap((prev) => ({ ...prev, [index]: false }));
      }
    }, 250);
  };

  const handleRowItemNameChange = (index: number, value: string) => {
    // A typed name is a manual line: whatever a picked suggestion set goes with
    // it — including its stock link, which the delivery slip moves stock by
    // (resolveClientLineStock matches inventoryItemId first). D81 review.
    updateItem(index, {
      itemName: value,
      sourceType: 'manual',
      supplierName: '',
      supplierVendorId: null,
      inventoryItemId: '',
    }, true);
    setActiveRowSuggestionIndex(index);
    searchRowSuggestions(index, value);
  };

  const applyRowSuggestion = (index: number, suggestion: LineItemSuggestion) => {
    // 2026-09-22 one-owner audit (defect #4) — the suggestion's own resolved
    // `currency` (usually TTD for inventory/catalogue rows) used to be computed
    // and thrown away here: whatever `suggestion.price` said went straight onto
    // `actualUnitCost`/`quoteUnitPrice` regardless of the invoice's currency.
    const { cost, refusalReason } = resolveImportedLineCost({
      invoiceCurrency: currency,
      sourceCurrency: suggestion.currency,
      sourcePrice: suggestion.price,
      rate: ttdRateFor(invoice?.items?.[index]), // D157.5: the line's pin, else the invoice's, else Settings
    });
    const existingRemarks = invoice?.items?.[index]?.internalRemarks || '';
    const note = refusalReason
      ? `[Suggestion import] ${refusalReason} — cost NOT applied, please review manually`
      : '';
    updateItem(index, {
      itemName: suggestion.title,
      // D152.5: a pick never writes the panel's second line (category, supplier, SKU) as the description; one a
      // person typed in the drawer stays (read from the line NOW, not this render's copy), anything else is blank.
      ...(invoiceRef.current?.items?.[index]?.descriptionAmended ? {} : { itemDescription: '' }),
      sourceType: suggestion.source,
      // D86: carry the source vendor id where it has one; a bare name is NOT carried (no guessing),
      // the supplier stays blank for the person to pick.
      supplierName: suggestion.supplierVendorId ? suggestion.supplier || '' : '', // never subtitle: that is a category path (D152.5); the server names it on save
      supplierVendorId: suggestion.supplierVendorId || null,
      unit: suggestion.unit || '',
      billUnit: suggestion.unit || '',
      actualUnitCost: cost,
      quoteUnitPrice: importedSellingPrice(cost), // D119.10: whole-cent price from the EXACT cost above
      // A cost a person picks is deliberate, as a typed one is: without it an order line's
      // old cost stays under the new selling price (D157.9: 'chosen', on every line).
      ...(cost != null ? personCostBasis('chosen') : {}),
      inventoryItemId: suggestion.source === 'inventory' ? suggestion.inventoryItemId || '' : '',
      ...(note ? { internalRemarks: existingRemarks ? `${existingRemarks}\n${note}` : note } : {}),
    });
    clearRowSuggestions(index);
  };

  const addBlankLineItem = () => {
    editInvoice((prev) => {
      if (!prev) return prev;
      const items = [...prev.items, buildBlankLineItem(nextLineNumber(prev.items || []))];
      return buildDraftFromInvoice({ ...prev, items });
    });
  };

  // D171.1: each picked bill line goes where the dialog says: a draft line or a new line. Bill lines onto one line are ONE
  // vendor row of their summed quantity at the weighted cost; addBillVendorRow never adds onto that vendor's row (D108.4).
  // A line marked Not supplied takes nothing (D170.7.4); a line in another unit is never merged onto.
  const importSelectedVendorBillLines = () => {
    const bill = pickedBill;
    if (!invoice || !bill) return;
    const lines = bill.extracted.lineItems;
    const { supplierName, supplierVendorId } = billSupplier(bill);
    let skippedMarked: number[] = [];
    let noQuantity: number[] = [];
    editInvoice((prev) => {
      if (!prev) return prev;
      skippedMarked = [];
      noQuantity = [];
      const nextItems = prev.items ? [...prev.items] : [];
      const onto = new Map<number, number[]>();
      selectedVendorBillLines.forEach((lineIndex: number) => {
        const lineItem = lines[lineIndex];
        if (!lineItem) return;
        const target = billLineTargets[lineIndex] ?? null;
        const existing = target == null ? undefined : nextItems[target];
        if (target == null || !existing) {
          // D157.5: a fresh line has no pin of its own: the invoice's rate, else Settings
          nextItems.push(buildLineItemFromVendorInvoice(bill, lineItem, bill.pairs[lineIndex], nextLineNumber(nextItems), currency, ttdRateFor()));
        } else if (isMarkedNotSupplied(existing)) {
          const no = existing.lineNumber ?? target + 1;
          if (!skippedMarked.includes(no)) skippedMarked.push(no);
        } else if (!billUnitsDiffer(bill, lineIndex, target)) {
          onto.set(target, [...(onto.get(target) || []), lineIndex]);
        }
      });
      onto.forEach((billLines, target) => {
        const existing = nextItems[target];
        const merged = mergedBillLines(billLines.map((i) => ({ quantity: bill.pairs[i].quantity, price: bill.pairs[i].unitPrice })));
        if (!merged) {
          noQuantity.push(existing.lineNumber ?? target + 1);
          return;
        }
        const { cost, refusalReason } = resolveImportedLineCost({
          invoiceCurrency: currency,
          sourceCurrency: bill.currency,
          sourcePrice: merged.price,
          rate: ttdRateFor(),
        });
        const note = refusalReason ? billImportNote(refusalReason) : '';
        nextItems[target] = {
          ...existing,
          // A bill's cost merged onto the line is a person's choice of cost (owner 2026-10-01, D157.9).
          ...(cost != null ? { actualUnitCost: cost, ...personCostBasis('chosen') } : {}),
          ...(existing.supplierName ? {} : { supplierName, supplierVendorId }),
          // D108.4: a nameless row (a seeded remainder) is filled, never paralleled (billed twice).
          vendorQuantities: addBillVendorRow(existing.vendorQuantities, { supplierName, supplierVendorId, quantity: merged.quantity }),
          // A refused cost still says why on the line.
          internalRemarks: note ? [existing.internalRemarks, note].filter(Boolean).join('\n') : existing.internalRemarks,
        };
      });
      return buildDraftFromInvoice({ ...prev, items: nextItems });
    });
    if (skippedMarked.length === 1) {
      toast.info(`Line ${skippedMarked[0]} is marked Not supplied: restore it to import this bill's quantity`);
    } else if (skippedMarked.length > 1) {
      toast.info(`Lines ${skippedMarked.join(', ')} are marked Not supplied: restore them to import this bill's quantities`);
    }
    if (noQuantity.length) {
      toast.info(`Nothing was merged onto line ${noQuantity.join(', ')}: the bill states no quantity above 0 for it`);
    }
    closeVendorBillModal();
  };

  const setBillLineTarget = (lineIndex: number, target: number | null) => {
    setBillLineTargets((prev) => prev.map((t, i) => (i === lineIndex ? target : t)));
    if (pickedBill && billUnitsDiffer(pickedBill, lineIndex, target)) {
      setSelectedVendorBillLines((prev) => prev.filter((i) => i !== lineIndex));
    }
  };

  const openVendorBillModal = () => {
    setShowVendorBillModal(true);
  };

  const clearPickedBill = () => {
    pickedBillId.current = null;
    setSelectedVendorBill(null);
    setSelectedVendorBillLines([]);
    setBillLineTargets([]);
  };

  const closeVendorBillModal = () => {
    setShowVendorBillModal(false);
    setVendorBillResults([]);
    setVendorBillError(null);
    clearPickedBill();
  };

  // A picked bill's lines come through the invoice's own route (Invoices edit), never the Vendors-gated read, paired
  // with the draft as it stands (D171.1). A line whose target is counted in another unit starts unticked.
  const pickVendorBill = async (row: CandidateInvoice) => {
    pickedBillId.current = row._id;
    setSelectedVendorBill({ row, bill: null, error: null });
    setSelectedVendorBillLines([]);
    setBillLineTargets([]);
    try {
      const bill = await fetchVendorBillForImport(row._id, draftLinesForPairing(invoice?.items || []));
      if (pickedBillId.current !== row._id) return;
      const targets = bill.pairs.map((p) => p.target);
      setSelectedVendorBill({ row, bill, error: null });
      setBillLineTargets(targets);
      setSelectedVendorBillLines(importableBillLines(bill, targets));
    } catch (e) {
      if (pickedBillId.current !== row._id) return;
      setSelectedVendorBill({ row, bill: null, error: `This bill's lines could not be loaded. ${describeApiError(e, { action: 'Load the bill' }).summary}` });
    }
  };

  const toggleVendorBillLine = (index: number) => {
    setSelectedVendorBillLines((prev) => {
      if (prev.includes(index)) return prev.filter((value) => value !== index);
      return [...prev, index].sort((a, b) => a - b);
    });
  };

  // The More menu's Reload throws the draft away, so it asks first when there is one.
  const reload = async () => {
    if (isDirty && !(await askDiscard())) return;
    await refreshFromServer();
  };
  const refreshFromServer = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const res =
        (await fetchClientInvoiceById(id).catch(() => null)) ||
        (await invoiceForOrder(id));
      adoptServerInvoice(res);
    } catch (e: any) {
      openFeedbackModal('error', 'Could not refresh', saveFailureMessage(e?.code, 'The invoice could not be refreshed. Your edits are still on screen.', e?.details));
    } finally {
      setSaving(false);
    }
  };

  const buildInvoicePatchPayload = (current: ClientInvoiceDto, nextStatus?: ClientInvoiceDto['status']) => {
    // A plain Save / Save Draft must never move a sent-onward or cancelled
    // invoice down to saved/draft (that re-opens the D56 charges lock).
    const holdsStatus = isInvoiceSentOnward(current.status) || current.status === 'cancelled';
    const next = buildDraftFromInvoice({
      ...current,
      status: holdsStatus ? current.status : nextStatus || current.status,
    });

    return {
      // D81 — the version this screen loaded; a save over a newer one is refused.
      expectedUpdatedAt: current.updatedAt,
      status: next.status,
      invoiceDate: next.invoiceDate,
      // Item 5: was `next.invoiceSection?.dueDate`, the frozen creation-time mirror —
      // reading it back meant a typed Due Date (which patches the TOP-LEVEL field,
      // like Invoice Date) was silently discarded on every save. Top-level is the
      // one the model, PDF and this tab all read (D98.14's "ONE date" rule, same as
      // invoiceDate above).
      dueDate: next.dueDate,
      notes: next.invoiceSection?.notes || next.notes,
      clientName: next.invoiceSection?.clientName || next.clientName,
      clientEmail: next.invoiceSection?.clientEmail || next.clientEmail,
      vesselName: next.invoiceSection?.vesselName || next.vesselName,
      currency: next.currency || next.invoiceSection?.currency,
      // Module 2 — header fields
      ownerName: (next as any).ownerName,
      ownerAddress: (next as any).ownerAddress,
      agentName: (next as any).agentName,
      // D153.6 — saved as shown: an invoice that stated no payer shows, and saves, the agent.
      payer: payerOf(next),
      agentAddress: next.agentAddress,
      clientPoNumber: (next as any).clientPoNumber,
      // D183.5: order info, on screen only; saved as shown.
      port: next.port,
      estimatedDeliveryDate: next.estimatedDeliveryDate,
      category: (next as any).category,
      // Item 5 — a free-text register column, same pattern as the other Module 2
      // header fields above (top-level, no invoiceSection mirror).
      paymentTerms: (next as any).paymentTerms,
      items: next.items.map(({ savedDispatchQuantity: _screenOnly, ...line }) => line),
      summary: next.summary,
      invoiceSection: { ...next.invoiceSection, currency: next.currency || next.invoiceSection?.currency },
      markupSection: next.markupSection,
      deliverySlipSection: next.deliverySlipSection,
      // D56 — charges ride along on every save, but ONLY while they are
      // still editable: `canEditInvoiceCharges(current)` — `current`, the
      // status/isAdditional BEFORE this save, not `next` — is what decides
      // that. patchClientInvoice's locks both read the invoice's OLD stored
      // fields, so a save that is itself sending the invoice right now
      // (nextStatus === 'sent') still carries the charges through in the
      // same request. Once charges are NOT editable (already sent-onward, OR
      // an ADDITIONAL invoice — D56 point 3, CLIENT_INVOICE_CHARGES_NOT_ON_ADDITIONAL),
      // leaving `charges` OUT entirely matters — sending the array
      // unconditionally would trip one of those locks on every OTHER field's
      // save too, since the server's gate fires whenever `charges` is
      // present in the body at all, whether or not it changed.
      ...(canEditInvoiceCharges(current) ? { charges: next.charges } : {}),
    };
  };

  // D98.14 - a cleared invoice date is refused on every path that saves the invoice; nothing guesses one back.
  // D109.6: a refused typed date is not emitted, so `invoice.invoiceDate` is still the OLD date. Refuse that too.
  const refuseBlankInvoiceDate = () => {
    if (!invoiceDateValid) {
      openFeedbackModal('error', 'Fix the invoice date', 'The invoice date is not a real day. Nothing was saved.');
      return true;
    }
    if (invoice?.invoiceDate) return false;
    openFeedbackModal('error', 'Pick the invoice date', 'Pick the invoice date. Nothing was saved.');
    return true;
  };

  // No nextStatus = a plain Save: the invoice keeps the status it has (a same-status move is allowed, D96.1).
  // "Use system cost": the system cost is worked out by the server (shipped stock, else the supplier bill, else the PO
  // price), so the click hands the line back and saves at once, and the screen then shows the restored cost and where it came
  // from. It used to wait for a Save, leaving the typed cost on screen under "The order's own cost". The effect runs after the
  // render that holds the edit, so the save sends the line with its basis cleared.
  const [saveForSystemCost, setSaveForSystemCost] = useState(false);
  const saveInvoice = async (nextStatus?: ClientInvoiceDto['status'], acknowledgeUndispatched = false) => {
    if (!invoice) return;
    if (refuseBlankInvoiceDate()) return;
    const unpicked = unpickedSupplierLines(invoice.items);
    if (unpicked.length) {
      // A feedback modal, not setError: that replaces the whole page and would lose the edits.
      openFeedbackModal('error', 'Pick the supplier', unpickedSupplierMessage(unpicked));
      return;
    }
    setSaving(true);
    try {
      const payload: ClientInvoicePatch = {
        ...buildInvoicePatchPayload(invoice, nextStatus),
        ...(acknowledgeUndispatched ? { acknowledgeUndispatched: true } : {}),
      };
      const res = await patchClientInvoice(invoice._id, payload);
      adoptServerInvoice(res);
      toast.success(nextStatus === 'sent' ? 'Marked as sent' : 'Saved');
    } catch (e: any) {
      // D56 — a charges-bearing save on an invoice `patchClientInvoice`
      // considers already sent (or a status only reachable via sent —
      // partially_paid, paid) is refused the same way, with
      // CLIENT_INVOICE_CHARGES_LOCKED; on an ADDITIONAL invoice (D56 point 3
      // — it bills at full price, with no discount and no charges, ever) the
      // same PATCH is refused with its own code,
      // CLIENT_INVOICE_CHARGES_NOT_ON_ADDITIONAL. `buildInvoicePatchPayload`
      // already leaves `charges` out of the body once `canEditInvoiceCharges`
      // says either is true, so this is a defensive path (another session
      // sending the invoice, or the page rendering a stale
      // `invoice.isAdditional`, between this screen's load and this Save),
      // not the normal one — and it is shown the same way, by message, for
      // the same reason.
      //
      // `clientInvoice.controller.js`'s patchInvoice is now `asyncHandler` +
      // the central error handler (docs/debt-closures/296.md's own
      // "suggested follow-up", closed by this change), so `e.code` is
      // populated for both refusals above. No `e.code` branch is added here
      // regardless: the AppError's message is already written for this exact
      // banner, and a branch that renders the same text a different way is
      // not a fix — only a call site that needs to BEHAVE differently per
      // code (a distinct UI treatment, not just different words) would earn
      // one.
      //
      // D81 — CLIENT_INVOICE_CHANGED does earn a branch: the invoice changed
      // under this screen (an order quantity change it followed, a slip,
      // another person's save), and the only way on is to reload it.
      // Owner 2026-10-01: marking sent with lines not dispatched warns, then
      // allows — the server refuses until the person confirms (the counts come
      // from the refusal's details, never its prose).
      if (e?.code === 'VENDOR_NOT_IN_LIST') {
        // Codes, not prose (invariant 6): the lines come from the refusal's details.
        const lines: number[] = Array.isArray(e?.details?.lines) ? e.details.lines : [];
        openFeedbackModal('error', 'Pick the supplier', lines.length ? unpickedSupplierMessage(lines) : 'Pick each supplier from the vendor list. Nothing was saved.');
        return;
      }
      if (e?.code === 'CLIENT_INVOICE_UNDISPATCHED_LINES') {
        // Counts missing or unreadable are null, never 0: the prompt then words
        // itself without numbers instead of saying "0 of 0 lines".
        const count = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
        setUndispatchedPrompt({ undispatched: count(e?.details?.undispatched), total: count(e?.details?.total) });
        return;
      }
      openFeedbackModal('error', 'Could not save', saveFailureMessage(e?.code, 'The invoice could not be saved. Your edits are still on screen; try again.', e?.details));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!saveForSystemCost) return;
    setSaveForSystemCost(false);
    void saveInvoice();
    // saveInvoice is rebuilt every render and reads this render's invoice, which holds the cleared basis.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveForSystemCost]);

  const openFeedbackModal = (tone: FeedbackModalState['tone'], title: string, message: React.ReactNode) => {
    setFeedbackModal({
      open: true,
      tone,
      title,
      message,
    });
  };

  const closeFeedbackModal = () => {
    setFeedbackModal((prev) => ({ ...prev, open: false }));
  };

  // Worksheet actions (additional invoice, writeback pick, exchange slip). One at a time: a second
  // click while one is in flight is dropped (the ref answers before React re-renders), so a double
  // click can never make two additional invoices or two exchange slips.
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const busyRef = useRef(false);
  const showApiError = (e: any, action: string) => {
    const words = refusalText(e?.code, e?.details);
    if (words) return openFeedbackModal('error', `${action} was refused`, words);
    const d = describeApiError(e, { action });
    openFeedbackModal(
      'error',
      d.title,
      <div className="space-y-2">
        <p>{d.summary}</p>
        {d.hint ? <p>{d.hint}</p> : null}
      </div>
    );
  };
  const runAction = async (name: string, work: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyAction(name);
    try {
      await work();
    } catch (e: any) {
      if (e?.code === 'CLIENT_INVOICE_CHANGED') {
        openFeedbackModal('error', 'Invoice changed', INVOICE_CHANGED_MESSAGE);
      } else if (SAVE_FAILURE_BY_CODE[String(e?.code)]) {
        openFeedbackModal('error', `${name} failed`, SAVE_FAILURE_BY_CODE[String(e.code)]);
      } else {
        showApiError(e, name);
      }
    } finally {
      busyRef.current = false;
      setBusyAction(null);
    }
  };
  // D98: the in-page confirm (was window.confirm). A local dialog, so the page needs no ConfirmProvider.
  const [additionalAsk, setAdditionalAsk] = useState(false);
  const askAdditionalInvoice = async () => {
    if (!invoice?._id || busyRef.current) return;
    // D98.6: creating it navigates away, so unsaved edits are asked about first.
    if (isDirty && !(await askDiscard())) return;
    setAdditionalAsk(true);
  };
  const createAdditionalInvoice = () => {
    setAdditionalAsk(false);
    if (!invoice?._id || busyRef.current) return;
    return runAction('Create additional invoice', async () => {
      const created = await createAdditionalClientInvoice(invoice._id);
      navigate(`/client-invoices/${created._id}`);
    });
  };
  // D157.17: a customs invoice - one typed fee. The dialog stays open on a refusal and says why.
  const [customsAsk, setCustomsAsk] = useState<{ amount: string; error: string } | null>(null);
  const askCustomsInvoice = async () => {
    if (!invoice?._id || busyRef.current) return;
    if (isDirty && !(await askDiscard())) return;
    setCustomsAsk({ amount: '', error: '' });
  };
  const createCustomsInvoice = async () => {
    if (!customsAsk || !invoice?._id || busyRef.current) return;
    const amount = num(customsAsk.amount);
    if (amount === null || amount <= 0) {
      setCustomsAsk({ ...customsAsk, error: SAVE_FAILURE_BY_CODE.CLIENT_INVOICE_CUSTOMS_AMOUNT_INVALID });
      return;
    }
    busyRef.current = true;
    setBusyAction('Create customs invoice');
    try {
      const created = await createAdditionalClientInvoice(invoice._id, amount);
      setCustomsAsk(null);
      navigate(`/client-invoices/${created._id}`);
    } catch (e: any) {
      const error = refusalText(e?.code, e?.details) || describeApiError(e, { action: 'Create customs invoice' }).summary || 'The customs invoice was not created.';
      setCustomsAsk({ ...customsAsk, error });
    } finally {
      busyRef.current = false;
      setBusyAction(null);
    }
  };
  // D98.12: the drawer's WritebackForm validates (askWriteback) and hands the answers here.
  // D96.2: a sent invoice's quantities are locked; the form then asks for the TT$ cost only.
  const writebackPick = (answers: WritebackAnswers) => {
    if (!invoice?._id || busyRef.current) return;
    return runAction('Writeback pick', async () => {
      const saved = await writebackProcurementPlanLine(invoice._id, { ...answers, expectedUpdatedAt: invoice.updatedAt });
      adoptServerInvoice(saved);
      openFeedbackModal(
        'success',
        'Writeback saved',
        answers.actualQuantity === undefined
          ? 'The TT$ cost was recorded on the procurement plan.'
          : 'The actual pick was recorded on the procurement plan.'
      );
    });
  };
  // D98.12: the answers come from ExchangeSlipForm on the Delivery tab (it validates with askExchange).
  const exchangeSlip = (answers: ExchangeAnswers) => {
    if (!invoice?._id || busyRef.current) return;
    return runAction('Exchange slip', async () => {
      const slip = await createClientInvoiceExchangeSlip(invoice._id, { swaps: [answers], expectedUpdatedAt: invoice.updatedAt });
      openFeedbackModal('success', 'Exchange slip created', `Exchange slip ${slip?.slipNumber || ''} was created.`);
      await refreshFromServer();
    });
  };

  // Told by err.code + err.details (the facts the server sends beside its message), never by
  // matching the message's English: a reworded sentence must not change what the dialog says.
  const formatDeliverySlipError = (error: any, editsSaved = false) => {
    // The slip saves the invoice first — the same stale-screen refusal as Save, unless the save
    // went through and the slip's own write found the invoice changed since.
    if (error?.code === 'CLIENT_INVOICE_CHANGED') {
      return { title: 'Invoice changed', body: editsSaved ? SLIP_INVOICE_CHANGED_MESSAGE : INVOICE_CHANGED_MESSAGE };
    }
    if (error?.code === 'DELIVERY_SLIP_NO_ROWS') {
      return {
        title: 'No delivery rows selected',
        body: 'Set a dispatch quantity on at least one row before creating a delivery slip.',
      };
    }
    if (error?.code === 'CLIENT_INVOICE_CANCELLED') {
      return { title: 'Invoice cancelled', body: 'This invoice is cancelled; a delivery slip cannot be made.' };
    }
    if (error?.code === 'DELIVERY_SLIP_INVOICE_SENT') {
      return { title: 'Invoice sent', body: 'This invoice has been sent; a delivery slip cannot be made.' };
    }
    // A refusal of the save the slip makes first (e.g. billed below shipped, D157.6) gets Save's words;
    // DELIVERY_SLIP_BAD_DISPATCH_QUANTITIES, _STOCK_VALIDATION and the rest: describeApiError words them.
    return {
      title: 'Failed to create delivery slip',
      body: refusalText(error?.code, error?.details) ?? describeApiError(error, { action: 'Create delivery slip' }).summary,
    };
  };

  const createDeliverySlip = async () => {
    if (!invoice) return;
    // A slip saves the invoice too: the same D98.14 refusal as Save, never a fallback date.
    if (refuseBlankInvoiceDate()) return;
    const rows = (invoice.items || []).filter((item) => toNumber(item.dispatchQuantity, 0) > 0);
    if (!rows.length) {
      openFeedbackModal(
        'error',
        'No delivery rows selected',
        'Set a dispatch quantity on at least one row before creating a delivery slip.'
      );
      return;
    }

    setCreatingSlip(true);
    let editsSaved = false;
    try {
      // F34 (U00038, QA run 2026-09-30): the staged dispatch quantities go IN the slip
      // request, not in this save, so a slip the server refuses stores none of them.
      // The save still keeps every other edit on the page.
      const stagedQuantities = (invoice.items || []).map((item) => toNumber(item.dispatchQuantity, 0));
      const withoutStaged = {
        ...invoice,
        items: (invoice.items || []).map((item) => ({ ...item, dispatchQuantity: item.savedDispatchQuantity ?? 0 })),
      };
      const persisted = await patchClientInvoice(invoice._id, buildInvoicePatchPayload(withoutStaged, invoice.status));
      editsSaved = true;
      setSavedInvoice(persisted); // the edits are stored even if the slip below is refused
      history.clear(); // saved: nothing before this point can be undone (D119.11)
      markInvoiceFresh();
      void refetchLive(persisted._id); // D183.6: the partial-success path adopts without adoptServerInvoice
      // The save stored the lines, so the slip request names them as stored.
      const staged = (persisted.items || []).map((item, index) => ({
        lineNumber: item.lineNumber ?? index + 1,
        dispatchQuantity: stagedQuantities[index] ?? 0,
      }));
      // Keep the staged quantities on screen while the slip is attempted.
      setInvoice(
        buildDraftFromInvoice(
          withEffectiveInvoiceDate(withSavedDispatch({
            ...persisted,
            items: (persisted.items || []).map((item, index) => ({ ...item, dispatchQuantity: stagedQuantities[index] ?? 0 })),
          }, persisted))
        )
      );
      const result = await createClientInvoiceDeliverySlip(invoice._id, { dispatchQuantities: staged });
      adoptServerInvoice(result.invoice);
      openFeedbackModal(
        'success',
        'Delivery slip created',
        [
          `Created delivery slip ${result.deliverySlip?.slipNumber || ''} for ${result.deliverySlip?.items?.length || 0} row(s).`,
          ...billedRiseNotes(persisted, result.invoice),
        ].join(' ')
      );
    } catch (e: any) {
      const formatted = formatDeliverySlipError(e, editsSaved);
      openFeedbackModal('error', formatted.title, formatted.body);
    } finally {
      setCreatingSlip(false);
    }
  };

  const exportCurrentSection = () => {
    if (!invoice) return;
    const invoiceNumber = invoice.invoiceNumber || 'client-invoice';
    const confirmedLabel = orderRef(confirmedOrder?.confirmedOrder) || '--';
    const clientLabel = partyClient;
    const vesselLabel = partyVessel;
    const headerRows: Array<Array<unknown>> = [
      ['Client Invoice Export'],
      ['Invoice Number', invoiceNumber],
      ['Confirmed Order', confirmedLabel],
      ['Client', clientLabel],
      ['Vessel', vesselLabel],
      ['Section', tab !== 'delivery' ? 'Invoice' : 'Delivery Slip'],
      [],
    ];

    let sectionRows: Array<Array<unknown>> = [];
    if (tab !== 'delivery') {
      sectionRows = [
        ['#', 'Item', 'Remarks', 'Source', 'Supplier', 'Confirmed Qty', 'Confirmed Qty Unit', 'Invoice Qty', 'Invoice Qty Unit', 'Cost Price', 'Selling Price', 'Markup %', 'Actual mark-up %', 'Line Total', 'PO Matches', 'Alerts'],
        ...(invoice.items || []).map((item, index) => {
          const pricing = getRowPricing(item);
          return [
            item.lineNumber ?? index + 1,
            item.itemName || '',
            String(item.clientRemarks || '').trim(),
            item.sourceType || '',
            item.supplierName || '',
            pricing.confirmedQty,
            pricing.confirmedQtyUnit,
            pricing.invoiceQty,
            pricing.invoiceQtyUnit,
            costInputText(pricing.costPrice), // the stored cost is exact (D119.10); stated as the Cost cell states it
            pricing.sellingPrice ?? '',
            pricing.markupPercent ?? '', // the typed rate while it holds, else the actual (D119.15)
            pricing.actualMarkupPercent ?? '',
            pricing.lineTotal ?? '',
            (item.poMatches || [])
              .map((match) => `${match.poNumber || '--'} (${poMatchQuantity(match)})`)
              .join(' | '),
            item.discrepancyAlert || '',
          ];
        }),
        [],
        // The invoice's own totals rows (buildInvoiceTotalsRows, the owner the on-screen block uses):
        // they foot to GRAND TOTAL, and there is no VAT row on a client invoice (D53).
        ...invoiceTotalsRows().map((row) => [row.label, row.kind === 'discount' ? -row.amount : row.amount]),
      ];
    } else {
      sectionRows = [
        ['#', 'Packing Item', 'Invoice Qty', 'Dispatched Qty', 'Remaining Qty', 'Dispatch Qty', 'Unit'],
        ...(invoice.items || []).map((item, index) => [
          item.lineNumber ?? index + 1,
          item.itemName || '',
          item.invoiceQuantity ?? item.confirmedQuantity ?? '',
          item.dispatchedQuantity ?? 0,
          remainingDispatchQty(item, creditedShortByLine.get(String(item.lineNumber ?? index + 1)) || 0),
          item.dispatchQuantity ?? 0,
          item.packingUnit || item.unit || '',
        ]),
      ];

      const slips = deliverySlipSection.slips || [];
      if (slips.length) {
        sectionRows.push([]);
        sectionRows.push(['Created Delivery Slips']);
        // D153.10: no slip quantity total - its rows mix units; each row states its own unit.
        sectionRows.push(['Slip #', 'Created At', 'Items']);
        slips.forEach((row: any) => {
          sectionRows.push([
            row.slipNumber || row.slipId || '',
            fmtDateTime(row.createdAt),
            Array.isArray(row.items)
              ? row.items
                  .map((slipItem: any) => {
                    if (!slipItem.isExchange) {
                      return `${slipItem.itemName || slipItem.itemDescription || 'Item'} x ${slipItem.quantity ?? 0}${slipItem.unit ? ` ${slipItem.unit}` : ''}`;
                    }
                    // An exchange row reads as its PDF prints it (D153.5): what went back, what came, why; no unit.
                    const { exchangeReturn: back, exchangeDelivery: sent } = slipItem;
                    const reason = String(slipItem.reason || '').trim();
                    return `Returned ${back?.itemName || ''} x ${back?.quantity ?? ''}, delivered ${sent?.itemName || ''} x ${sent?.quantity ?? ''}${reason ? ` (${reason})` : ''}`;
                  })
                  .join(' | ')
              : '',
          ]);
        });
      }
    }

    downloadRowsCsv(
      `client-invoice-${invoiceNumber}-${tab === 'delivery' ? 'delivery' : 'invoice'}.csv`,
      [...headerRows, ...sectionRows]
    );
  };

  const summary = invoice?.summary || {};
  const invoiceTotalsRows = () =>
    buildInvoiceTotalsRows({
      quotedTotal: summary.quotedTotal,
      discountPercentage: invoice?.discountPercentage,
      discountAmount: summary.discountAmount,
      charges: invoice?.charges,
      invoiceGrandTotal: summary.invoiceGrandTotal,
    });
  const deliverySlipSection = invoice?.deliverySlipSection || {
    shipTo: '',
    packingList: [],
    slips: [],
    returnedItems: [],
  };
  const hasLineItems = (invoice?.items || []).length > 0;
  // D98.3 - ONE rule for what is wrong with the invoice (lineIssues.ts) feeds the issues bar, the grid
  // dots, the drawer, the Mark-sent gate and the J8 alert history. Nothing else re-derives it.
  const orderTotalFor = (inv: ClientInvoiceDto | null | undefined, cur: string) => ({
    invoiceGrandTotal: inv?.summary?.invoiceGrandTotal,
    invoiceCurrency: cur,
    orderTotal: confirmedOrder?.confirmedOrder?.totalQuote,
    orderCurrency: confirmedOrder?.confirmedOrder?.currency,
  });
  const issues = useMemo(
    () => analyseInvoice({ items: invoice?.items, orderTotal: orderTotalFor(invoice, currency) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [invoice, currency, confirmedOrder]
  );
  const unpickedLines = unpickedSupplierLines(invoice?.items);
  const blockingIssues = issues.lines.flatMap((l) => l.filter((i) => i.severity === 'block'));
  // Helpful disabled-reason: the first thing to fix, and how many more there are.
  const blockedReason = blockingIssues.length
    ? `${blockingIssues[0].message}${blockingIssues.length > 1 ? ` (+${blockingIssues.length - 1} more)` : ''} - fix before marking sent`
    : undefined;
  const hasBlockingLineItems = hasBlockingIssue(issues);

  // J8/J9 - alerts the user dismissed (server history) are hidden from the bar.
  const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
  const alertHistory = (invoice as any)?.alertHistory;
  useEffect(() => {
    const history: any[] = Array.isArray(alertHistory) ? alertHistory : [];
    setDismissedKeys(new Set(history.filter((h) => h.dismissedAt).map((h) => h.alertKey)));
  }, [alertHistory]);
  // J8 - sync the alerts of the SAVED invoice into alertHistory (idempotent by alertKey). Never the
  // draft's: an unsaved edit that raises an alert is not a figure the invoice stands on. Recording is a
  // write: only an editor, never on a cancelled invoice (D98.13 / D96.18). Debounced.
  const savedAlerts = useMemo(
    () =>
      savedInvoice
        ? alertsOf(analyseInvoice({ items: savedInvoice.items, orderTotal: orderTotalFor(savedInvoice, savedCurrency) }), savedInvoice.items)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [savedInvoice, savedCurrency, confirmedOrder]
  );
  const alertsSignature = savedAlerts.map((a) => a.alertKey).sort().join('~');
  const canRecordAlerts = canEditInvoice && invoice?.status !== 'cancelled';
  const alertsInvoiceId = invoice?._id;
  useEffect(() => {
    if (!canRecordAlerts || !alertsInvoiceId || savedAlerts.length === 0) return;
    const handle = window.setTimeout(() => {
      syncClientInvoiceAlerts(alertsInvoiceId, savedAlerts).catch((e) =>
        console.warn('[alerts/sync] not recorded:', e?.code || e?.status || e?.message)
      );
    }, 800);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRecordAlerts, alertsInvoiceId, alertsSignature]);
  const dismissAlert = async (alertKey: string) => {
    let reason = '';
    const ok = await confirm({
      title: 'Dismiss this alert?',
      summary: 'It is recorded with your name and reason, and stays in the audit trail.',
      body: (
        <label className="block text-xs text-slate-600">
          Reason (optional)
          <textarea
            aria-label="Dismiss reason"
            rows={2}
            onChange={(e) => {
              reason = e.target.value;
            }}
            className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
          />
        </label>
      ),
      confirmLabel: 'Dismiss',
    });
    if (!ok || !alertsInvoiceId) return;
    setDismissedKeys((prev) => new Set(prev).add(alertKey)); // optimistic
    // An alert only the unsaved draft raises is not in the history: nothing to record yet.
    if (!savedAlerts.some((a) => a.alertKey === alertKey)) return;
    try {
      await dismissClientInvoiceAlert(alertsInvoiceId, alertKey, reason);
    } catch (e: any) {
      // The server did not record it: put the alert back rather than hide what is still raised.
      setDismissedKeys((prev) => {
        const next = new Set(prev);
        next.delete(alertKey);
        return next;
      });
      openFeedbackModal('error', 'Alert not dismissed', `This alert could not be dismissed. ${describeApiError(e, { action: 'Dismiss alert' }).summary}`);
    }
  };

  // Lines tab view state: the grid filters and the name search (the drawer's line is declared above the history).
  const [issueFilter, setIssueFilter] = useState<number[] | null>(null);
  const [notSuppliedOnly, setNotSuppliedOnly] = useState(false);
  const [lineSearch, setLineSearch] = useState('');
  const notSuppliedCount = issues.lines.filter((l) => l.some((i) => i.code === 'not_supplied')).length;
  const visibleIndexes = useMemo(() => {
    const q = lineSearch.trim().toLowerCase();
    if (!issueFilter && !notSuppliedOnly && !q) return null;
    return (invoice?.items || [])
      .map((_, i) => i)
      .filter(
        (i) =>
          (!issueFilter || issueFilter.includes(i)) &&
          (!notSuppliedOnly || issues.lines[i]?.some((x) => x.code === 'not_supplied')) &&
          (!q || String(invoice?.items[i]?.itemName || '').toLowerCase().includes(q))
      );
  }, [invoice, issues, issueFilter, notSuppliedOnly, lineSearch]);
  const lineSeverities = (invoice?.items || []).map((it, i) => lineStatus(issues.lines[i] || [], it.completedTick));

  // Source quantities (Pantry / Stock / Vendor 1-3) from the drawer. Each branch writes the total it implies.
  const changeSourceQty = (index: number, field: SourceQtyField, next: number) => {
    const item = invoice?.items[index];
    if (!item) return;
    const sum = (list: Array<{ quantity?: number }>) => list.reduce((s, v) => s + toNumber(v?.quantity, 0), 0);
    if (field === 'pantry') {
      const total = next + toNumber(item.qtyFromStock, 0) + sum(item.vendorQuantities || []);
      updateItem(index, { qtyFromPantry: next, totalQtyBilled: total, invoiceQuantity: total }, 'pantry');
    } else if (field === 'stock') {
      const total = toNumber(item.qtyFromPantry, 0) + next + sum(item.vendorQuantities || []);
      updateItem(index, { qtyFromStock: next, totalQtyBilled: total, invoiceQuantity: total }, 'stock');
    } else {
      const slot = Number(field.slice('vendor'.length));
      const list = [...(item.vendorQuantities || [])];
      while (list.length <= slot) list.push({ vendorName: '', quantity: 0 });
      list[slot] = { ...list[slot], quantity: next };
      const total = toNumber(item.qtyFromPantry, 0) + toNumber(item.qtyFromStock, 0) + sum(list);
      updateItem(index, { vendorQuantities: list, totalQtyBilled: total, invoiceQuantity: total }, field); // 'vendor0' / 'vendor1' / ...: one slot's burst, not another's
    }
  };
  const unpickedReason = unpickedLines.length ? unpickedSupplierMessage(unpickedLines).replace(' Nothing was saved.', '') : undefined;
  // D96.2 — once sent / partially paid / paid the lines and money are read-only (a credit note
  // is the way to change them); remarks, dates and header text stay editable.
  // A cancelled invoice is final (D96.1): its worksheet is as read-only as a sent one.
  const isCancelled = invoice?.status === 'cancelled';
  const linesLocked = isInvoiceSentOnward(invoice?.status) || isCancelled;
  // D96.1 — a status button is offered only when the shared graph allows that move.
  const canGoSent = canMoveClientInvoiceStatus(invoice?.status, 'sent');

  // The one way this page opens a server print (D98.7, D153.5). A print is of the SAVED invoice, so
  // unsaved changes refuse it, except a slip, which prints what was saved on it (owner S5c); a coded
  // refusal shows its own words.
  const openServerPrint = async (
    action: string,
    fetchPrint: (id: string) => Promise<Blob>,
    { evenIfUnsaved = false }: { evenIfUnsaved?: boolean } = {},
  ) => {
    if (!invoice?._id) return;
    if (isDirty && !evenIfUnsaved) {
      openFeedbackModal('error', 'Unsaved changes', 'Save your changes first, then print.');
      return;
    }
    const id = invoice._id;
    await openPrintInNewTab(() => fetchPrint(id), {
      onBlocked: () => openFeedbackModal('error', 'Pop-up blocked', `Allow pop-ups for this site, then press ${action} again.`),
      onError: (e) => showApiError(e, action),
    });
  };
  // The server-printed TAX INVOICE (D153.4)
  const PDF_TITLE = "Download the tax invoice PDF: 25 lines a page, with 'Balance carried / brought forward' between pages";
  const downloadPdf = () => openServerPrint('Download PDF', fetchClientInvoicePdf);
  // The server-printed PACKING LIST (D153.5): the invoice's lines on the invoice's pages, no prices
  const printPackingList = () => openServerPrint('Print packing list', fetchClientInvoicePackingListPdf);
  // The server-printed ITEMS NOT SUPPLIED list for the captain (D170.7), never on the invoice
  const printNotSupplied = () => openServerPrint('Print for captain', fetchClientInvoiceNotSuppliedPdf);
  // One server-printed DELIVERY (or EXCHANGE) SLIP (D153.5)
  // A customs invoice's slip is built from the invoice, so it prints like a saved one (D157.17)
  const printCustomsSlip = () => openServerPrint('Print customs slip', fetchClientInvoiceCustomsSlipPdf, { evenIfUnsaved: true });
  const printIndividualSlip = (slip: { slipId: string }) =>
    openServerPrint('Print slip', (id) => fetchClientInvoiceDeliverySlipPdf(id, slip.slipId), { evenIfUnsaved: true });
  // One delivery slip's PACKING LIST (D158.2): prints what the slip saved, so unsaved edits do not matter
  const printSlipPackingList = (slip: { slipId: string }) =>
    openServerPrint('Print packing list', (id) => fetchClientInvoiceSlipPackingListPdf(id, slip.slipId), { evenIfUnsaved: true });

  if (loading) {
    return <div className="h-full"><LoadingState text="Loading invoice..." /></div>;
  }

  if (!invoice) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-red-600">
        {error || 'Invoice not found'}
      </div>
    );
  }

  // D98.1 - the drawer for the selected line: the page's EXISTING handlers, nothing new in the money path.
  const renderDrawer = () => {
    const index = selectedLine;
    const item = index != null ? invoice.items[index] : undefined;
    if (index == null || !item) return null;
    const p = getRowPricing(item);
    const sourceTotal =
      toNumber(item.qtyFromPantry, 0) +
      toNumber(item.qtyFromStock, 0) +
      (item.vendorQuantities || []).reduce((s, v) => s + toNumber(v?.quantity, 0), 0);
    // Blank when the line is unpriced or has no cost - never (0 - cost) x qty.
    const markupAmount =
      item.markupAmount != null
        ? Number(item.markupAmount)
        : p.sellingPrice == null || p.costPrice == null
          ? null
          : (p.sellingPrice - p.costPrice) * (billedQuantity(item) ?? 0);
    const lineIssues = issues.lines[index] || [];
    const drawerIssues: DrawerIssue[] = lineIssues.map((i) => ({ severity: i.severity === 'block' ? 'error' : 'warning', message: i.message }));
    const suggestions = rowSuggestionMap[index] || [];
    // D171.4: what the supplier billed, for the drawer's one click (`hidesSupplierBilled` says when it is not offered).
    const supplierBilled = hidesSupplierBilled(item, index) ? null : item.supplierBilledQty ?? null;
    const supplierBilledVs = supplierBilled == null ? null : item.supplierBilledVs ?? 'short'; // D172.5; a line stored before it is short
    return (
      <div className="no-print">
        <LineDrawer
          topOffset={headerBottom}
          item={item}
          index={index}
          total={invoice.items.length}
          currency={currency}
          issues={drawerIssues}
          readOnly={isCancelled}
          linesLocked={linesLocked && !isCancelled}
          removalLock={lineRemovalLock(item, index, creditNotes)}
          quoted={quoteInfoFor(item, confirmedOrder?.confirmedOrder?.items?.[item.confirmedOrderItemIndex ?? -1], money)}
          pricing={{
            costText: costInputText(p.costPrice),
            costEntry: costEntryFor(currency, ttdRateFor(item)), // D152.2: the drawer and the grid both read the one decision
            sellingPriceText: formatEditableNumber(p.sellingPrice),
            markupText: p.markupPercent == null ? '' : formatEditableNumber(p.markupPercent),
            makesMarkupPercent: p.makesMarkupPercent,
            hasCost: (p.costPrice ?? 0) > 0,
            markupAmount: markupAmount !== null && Number.isFinite(markupAmount) ? markupAmount : null,
            lineTotal: p.lineTotal,
            // D119.9: only on a US$ invoice (a TT$ invoice's Cost is already TT$; no currency yet states nothing, D96.14).
            ttdCost: (() => {
              const ttd = ttdCostFor(item, p.costPrice);
              return ttd && { text: ttd.text, disabledReason: ttd.reason };
            })(),
            ttdCostBefore: ttdCostBeforeFor(item, p.costPrice),
          }}
          totalBilled={sourceTotal > 0 ? sourceTotal : item.invoiceQuantity ?? 0}
          supplierBilled={supplierBilled}
          supplierBilledVs={supplierBilledVs}
          remainingDispatch={remainingDispatchQty(item, creditedShortByLine.get(String(item.lineNumber ?? index + 1)) || 0)}
          slipHistory={slipRowsForLine(deliverySlipSection.slips, item.lineNumber ?? index + 1)}
          money={money}
          poMatchQuantity={poMatchQuantity}
          writebackBusy={busyAction === 'Writeback pick'}
          canWriteback={canEditInvoice}
          notSupplied={lineIssues.some((i) => i.code === 'not_supplied')}
          onItemNameFocus={() => setActiveRowSuggestionIndex(index)}
          nameExtra={
            activeRowSuggestionIndex === index && (rowSuggestionLoadingMap[index] || suggestions.length > 0) ? (
              <div className="space-y-2 pt-1">
                {rowSuggestionLoadingMap[index] && <div className="text-xs text-slate-500">Loading suggestions...</div>}
                {suggestions.length > 0 && (
                  <div className="grid max-h-40 grid-cols-1 gap-2 overflow-auto pr-1">
                    {suggestions.map((suggestion) => (
                      <button
                        key={suggestion.key}
                        onClick={() => applyRowSuggestion(index, suggestion)}
                        className="rounded-md border border-slate-200 bg-white px-3 py-2 text-left hover:border-cyan-300 hover:bg-cyan-50"
                      >
                        <div className="text-xs font-semibold text-slate-800">{suggestion.title}</div>
                        <div className="text-xs text-slate-500">{suggestion.subtitle || suggestion.description || '--'}</div>
                        <div className="text-xs text-slate-500">
                          {suggestion.source === 'supplier' ? 'Vendor Bill' : suggestion.source === 'catalogue' ? 'Master Catalog' : 'Inventory'}
                          {suggestion.unit ? ` • ${suggestion.unit}` : ''}
                          {suggestion.price != null ? ` • ${money(suggestion.price, suggestion.currency)}` : ''}
                          {/* D157.5: a price with no stated currency is refused on pick; the row says so first. */}
                          {suggestion.price != null && !suggestion.currency ? ' in a currency this row does not state' : ''}
                        </div>
                        {suggestion.source === 'supplier' && <div className="text-xs text-slate-400">{SET_ON_THIS_LINE_NOTE}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null
          }
          updateItem={updateItem}
          onItemNameChange={handleRowItemNameChange}
          updateCostPrice={updateCostPrice}
          updateTtdCost={updateTtdCost}
          updateMarkup={updateMarkup}
          updateSellingPrice={updateSellingPrice}
          applyUomChange={applyUomChange}
          onUseSystemCost={(i) => {
            updateItemPricing(i, { costBasis: null });
            setSaveForSystemCost(true);
          }}
          removeItem={removeItem}
          onSourceQtyChange={(field, value) => changeSourceQty(index, field, value)}
          onWriteback={writebackPick}
          onUndo={history.undo}
          onRedo={history.redo}
          canUndo={history.canUndo}
          canRedo={history.canRedo}
          onPrev={() => setSelectedLine(Math.max(0, index - 1))}
          onNext={() => setSelectedLine(Math.min(invoice.items.length - 1, index + 1))}
          onClose={() => setSelectedLine(null)}
        />
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <Modal
        isOpen={currencyPrompt !== null}
        onClose={() => setCurrencyPrompt(null)}
        title="Change currency"
        maxWidthClass="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-800">
            {`Convert every price and cost from ${savedCurrency} to ${currencyPrompt} at the exchange rate already fixed on this invoice?`}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCurrencyPrompt(null)}
              className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void confirmCurrencyConversion()}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Convert
            </button>
          </div>
        </div>
      </Modal>

      <ActionDialog
        isOpen={additionalAsk}
        tone="confirm"
        title="Create an additional invoice?"
        summary="A new additional invoice is raised against the same client PO."
        confirmLabel="Create"
        onClose={() => setAdditionalAsk(false)}
        onCancel={() => setAdditionalAsk(false)}
        onConfirm={createAdditionalInvoice}
      />

      <ActionDialog
        isOpen={customsAsk !== null}
        tone="confirm"
        title="Create a customs invoice?"
        summary="A new additional invoice, category Customs, billing one CUSTOMS EXPENSE line for the fee you type."
        body={customsAsk ? (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Customs fee{invoice?.currency ? ` (${invoice.currency})` : ''}
              <input
                type="text"
                inputMode="decimal"
                autoFocus
                aria-label="Customs fee"
                value={customsAsk.amount}
                onChange={(e) => setCustomsAsk({ amount: e.target.value, error: '' })}
                className="mt-1 h-9 w-full rounded border border-slate-200 px-2 text-sm"
              />
            </label>
            {customsAsk.error ? <p role="alert" className="text-sm text-red-700">{customsAsk.error}</p> : null}
          </div>
        ) : null}
        confirmLabel="Create"
        onClose={() => setCustomsAsk(null)}
        onCancel={() => setCustomsAsk(null)}
        onConfirm={createCustomsInvoice}
      />

      <Modal
        isOpen={undispatchedPrompt !== null}
        onClose={() => setUndispatchedPrompt(null)}
        title="Lines not dispatched"
        maxWidthClass="max-w-lg"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-800">
            {undispatchedPrompt?.undispatched != null && undispatchedPrompt?.total != null
              ? `${undispatchedPrompt.undispatched} of ${undispatchedPrompt.total} lines are not dispatched yet. No slip can be made after sending. Mark sent anyway?`
              : 'Some lines are not dispatched yet. No slip can be made after sending. Mark sent anyway?'}
          </p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setUndispatchedPrompt(null)}
              className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setUndispatchedPrompt(null);
                void saveInvoice('sent', true);
              }}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Mark sent anyway
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={feedbackModal.open}
        onClose={closeFeedbackModal}
        title={feedbackModal.title}
        maxWidthClass="max-w-lg"
      >
        <div className="space-y-4">
          <div
            className={`rounded-xl border px-4 py-3 ${
              feedbackModal.tone === 'success'
                ? 'border-cyan-200 bg-cyan-50 text-cyan-900'
                : 'border-rose-200 bg-rose-50 text-rose-900'
            }`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`mt-0.5 flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${
                  feedbackModal.tone === 'success'
                    ? 'bg-cyan-100 text-cyan-700'
                    : 'bg-rose-100 text-rose-700'
                }`}
              >
                {feedbackModal.tone === 'success' ? '✓' : '!'}
              </div>
              <div className="min-w-0 text-sm leading-6">
                {typeof feedbackModal.message === 'string' ? <p>{feedbackModal.message}</p> : feedbackModal.message}
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={closeFeedbackModal}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={showVendorBillModal}
        onClose={closeVendorBillModal}
        title="Import Vendor Bill"
        maxWidthClass="max-w-6xl"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-500">{SET_ON_THIS_LINE_NOTE}</p>
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-4">
            <div className="space-y-3">
              <div className="text-sm font-semibold text-slate-800">Search vendor bills</div>
              <input
                value={vendorBillSearch}
                onChange={(e) => {
                  setVendorBillSearch(e.target.value);
                  clearPickedBill();
                }}
                placeholder="Search by vendor name, file name, or invoice number"
                className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm bg-white"
              />
              {vendorBillLoading && <div className="text-xs text-slate-500">Loading vendor bills...</div>}
              <div className="space-y-2 max-h-[55vh] overflow-auto pr-1">
                {vendorBillError ? (
                  <div className="flex items-center gap-3 text-xs text-rose-600">
                    <span>{vendorBillError}</span>
                    <button
                      type="button"
                      onClick={() => setVendorBillAttempt((n) => n + 1)}
                      className="h-7 px-3 rounded-md border border-rose-300 bg-white text-rose-700 font-semibold hover:bg-rose-50"
                    >
                      Retry
                    </button>
                  </div>
                ) : vendorBillResults.length > 0 ? (
                  vendorBillResults.map((row) => (
                    <button
                      key={row._id}
                      type="button"
                      onClick={() => pickVendorBill(row)}
                      className={`w-full text-left flex items-center gap-3 rounded-md border px-3 py-2 text-xs transition ${
                        selectedVendorBill?.row._id === row._id ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200 bg-slate-50 hover:bg-white'
                      }`}
                    >
                      <CandidateInvoiceCells vi={row} matchLabel="Matches this order" />
                    </button>
                  ))
                ) : vendorBillLoading ? null : vendorBillSearch.trim() ? (
                  <div className="text-xs text-slate-500">No vendor bills found.</div>
                ) : (
                  <div className="text-xs text-slate-500">
                    {invoice?.confirmedOrderId ? 'No vendor bills are on this order yet. ' : ''}Search for a vendor bill to load its line items.
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-sm font-semibold text-slate-800">Bill lines</div>
              {selectedVendorBill ? (
                <>
                  <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    <CandidateInvoiceCells vi={selectedVendorBill.row} matchLabel="Matches this order" />
                  </div>
                  {selectedVendorBill.error ? (
                    <div className="flex items-center gap-3 text-xs text-rose-600">
                      <span>{selectedVendorBill.error}</span>
                      <button
                        type="button"
                        onClick={() => pickVendorBill(selectedVendorBill.row)}
                        className="h-7 px-3 rounded-md border border-rose-300 bg-white text-rose-700 font-semibold hover:bg-rose-50"
                      >
                        Retry
                      </button>
                    </div>
                  ) : !pickedBill ? (
                    <div className="text-xs text-slate-500">Loading the bill's lines...</div>
                  ) : (
                <>
                  {pickedBill.currency == null && (
                    <div className="text-xs text-amber-700">This bill states no currency, so its prices are shown bare and not imported as a cost.</div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedVendorBillLines(importableBillLines(pickedBill, billLineTargets))}
                      className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedVendorBillLines([])}
                      className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700"
                    >
                      Clear
                    </button>
                    <button
                      type="button"
                      onClick={importSelectedVendorBillLines}
                      disabled={!selectedVendorBillLines.length}
                      className="px-3 py-2 rounded-md bg-primary-500 text-white text-xs font-semibold disabled:opacity-60"
                    >
                      Import Selected
                    </button>
                  </div>
                  <div className="space-y-2 max-h-[55vh] overflow-auto pr-1">
                    {pickedBill.extracted.lineItems.map((lineItem: any, idx: number) => {
                      // The bill's OWN currency as the server states it (the row's); none: a bare figure, never TT$.
                      const billCurrency = pickedBill.currency || '';
                      const checked = selectedVendorBillLines.includes(idx);
                      // D171.1: as reviewed (the server's pair), the figures a merge or a new line bills; none stated shows none.
                      // Missing is not zero: no price stated shows none (invariant 8).
                      const { quantity: qty, unit, unitPrice } = pickedBill.pairs[idx];
                      // 2026-09-22 one-owner audit — vessels are never charged
                      // VAT (BUSINESS_RULES#client-invoice-line-vat). This
                      // preview picks lines to import onto a CLIENT invoice, and
                      // `buildLineItemFromVendorInvoice` never carries the
                      // vendor bill's own tax onto that line any more, so a
                      // VAT chip here would show a rate the import then drops —
                      // dropped rather than shown and contradicted. The vendor
                      // bill's own VAT (input VAT, USC's cost) is still on the
                      // vendor-invoice page. D172.8: it IS inside the "Cost incl. VAT" chip,
                      // since the import costs the line with its VAT, as the billed rung does.
                      const printedTotal = num(lineItem?.lineTotal);
                      const lineTotal = printedTotal ?? (unitPrice == null || qty == null ? null : Number((unitPrice * qty).toFixed(2)));
                      const supplierName = selectedVendorBill.row.vendorName || '--';
                      // D171.1: where it goes ("→ Line N <name>" or a new line), changeable; never onto a line in another unit.
                      const target = billLineTargets[idx] ?? null;
                      const unitsDiffer = billUnitsDiffer(pickedBill, idx, target);
                      return (
                        <label
                          key={`${selectedVendorBill.row._id}-${idx}`}
                          className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer ${checked ? 'border-cyan-300 bg-cyan-50' : 'border-slate-200 bg-white'}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={unitsDiffer}
                            onChange={() => toggleVendorBillLine(idx)}
                            className="mt-1"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-slate-800">{lineItem.description || lineItem.itemName || 'Line Item'}</div>
                            <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-600">
                              <span className="rounded-full bg-slate-100 px-2 py-0.5">Qty {qty ?? '--'}</span>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5">{unit || '--'}</span>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5">Cost incl. VAT {money(unitPrice, billCurrency)}</span>
                              <span className="rounded-full bg-slate-100 px-2 py-0.5">{printedTotal != null ? 'Printed total' : 'Total incl. VAT'} {money(lineTotal, billCurrency)}</span>
                            </div>
                            <div className="mt-1 text-xs text-slate-500">Supplier {supplierName}</div>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                              <select
                                aria-label={`Bill line ${idx + 1} goes to`}
                                value={target ?? ''}
                                onChange={(e) => setBillLineTarget(idx, e.target.value === '' ? null : Number(e.target.value))}
                                className="h-7 rounded-md border border-slate-200 bg-white px-2"
                              >
                                <option value="">New line</option>
                                {(invoice?.items || []).map((it, pos) => (
                                  <option key={pos} value={pos}>{`→ Line ${it.lineNumber ?? pos + 1} ${it.itemName || it.itemDescription || ''}`.trim()}</option>
                                ))}
                              </select>
                              {unitsDiffer && (
                                <span className="text-amber-700">
                                  units differ ({unit} vs {billedUnitOf(invoice!.items[target!])})
                                </span>
                              )}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </>
                  )}
                </>
              ) : (
                <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                  Select a vendor bill from the left to review and import its items.
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>
      {/* Every print is a server PDF (D98.7, D153.5); a browser print of the screen leaves out the controls. */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body { background: #fff !important; }
          .no-print { display: none !important; }
        }
      `}</style>
      <InvoiceHeader
        containerRef={headerRef}
        invoiceNumber={invoice.invoiceNumber}
        status={invoice.status}
        isAdditional={Boolean((invoice as any).isAdditional)}
        unsavedChanges={isDirty}
        refresh={{ text: refreshStatus.text, tone: refreshStatus.tone, busy: refreshing, onClick: () => { void refreshInBackground(true); } }}
        onBack={(e) => guardLeave(e, '/client-invoices')}
        info={<ModuleInfoButton content={PAGE_INFO} ariaLabel="Client invoice flow information" />}
        save={
          isCancelled
            ? null
            : {
                label: saving ? 'Saving...' : 'Save',
                onClick: () => saveInvoice(),
                disabled: saving || unpickedLines.length > 0 || !currency,
                title: !currency ? CURRENCY_PICK_PROMPT : unpickedReason,
              }
        }
        primary={
          isCancelled
            ? null
            : isInvoiceSentOnward(invoice.status)
              ? { label: 'Download PDF', onClick: downloadPdf, title: PDF_TITLE }
              : {
                  label: 'Mark Sent',
                  onClick: () => saveInvoice('sent'),
                  disabled: saving || !canGoSent || !hasLineItems || hasBlockingLineItems || !currency,
                  title:
                    (!currency ? CURRENCY_PICK_PROMPT : undefined) ??
                    (!hasLineItems ? 'Add at least one line item before marking sent' : blockedReason),
                }
        }
        moreItems={[
          // The primary button already is Download PDF once the invoice is sent onward.
          { key: 'pdf', label: 'Download PDF', onClick: downloadPdf, title: PDF_TITLE, hidden: isInvoiceSentOnward(invoice.status) && !isCancelled },
          { key: 'print-packing', label: 'Print Packing List', onClick: printPackingList, title: "Open the packing list PDF (no prices): the invoice's lines on the invoice's pages" },
          { key: 'csv', label: 'Export CSV', onClick: exportCurrentSection },
          { key: 'refresh', label: 'Reload', onClick: reload },
          // Module 28 — Create additional invoice against same PO
          { key: 'additional', label: '+ Additional Invoice', onClick: askAdditionalInvoice, disabled: busyAction !== null, title: 'Module 28 — for items added after the master signed', hidden: !(canCreateInvoice && !(invoice as any).isAdditional && invoice.status !== 'cancelled') },
          // D157.17 — the customs fee as its own invoice
          { key: 'customs', label: '+ Customs Invoice', onClick: askCustomsInvoice, disabled: busyAction !== null, title: 'Bill a customs fee as its own invoice', hidden: !(canCreateInvoice && !(invoice as any).isAdditional && invoice.status !== 'cancelled') },
          // B6 — Procurement plan writeback; a cancelled invoice takes none (D96.12)
          // Credit notes are gated by the invoices screen's Create permission (creditNote.routes.js); none on a cancelled invoice (D96.18).
          // Item 15 (9 Oct 2026 QA retest): carries this invoice so the new-note form opens pre-filled, not blank.
          { key: 'credit-note', label: 'Raise credit note', to: newCreditNotePath(invoice._id), onNavigate: guardLeave, hidden: isCancelled || !canCreateInvoice },
        ]}
        client={partyClient}
        vessel={partyVessel}
        invoiceDate={fmtDate(invoice.invoiceDate, '--')}
        clientPo={(invoice as any).clientPoNumber || '--'}
        port={invoice.port || ''}
        estDelivery={invoice.estimatedDeliveryDate ? fmtDate(invoice.estimatedDeliveryDate, '--') : ''}
        currencyControl={
          <span className="flex items-center gap-2" data-testid="invoice-currency-block">
            {!currency ? (
              <span className="font-semibold text-rose-700">{CURRENCY_PICK_PROMPT}</span>
            ) : (
              <span>Invoice currency</span>
            )}
            <select
              data-testid="invoice-currency-select"
              aria-label="Invoice currency"
              value={currency}
              onChange={(e) => onCurrencyPick(e.target.value)}
              disabled={currencyLocked || !canEditInvoice || busyAction !== null}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 disabled:bg-slate-100 disabled:text-slate-500"
            >
              {!currency ? <option value="">Choose...</option> : null}
              <option value="TTD">TTD</option>
              <option value="USD">USD</option>
            </select>
          </span>
        }
        chain={
          /*
            Was: "Confirmed order: QUO-00015" printed as dead text while
            `quotationId` sat in the same object. The chain is now walkable,
            and the hops with nothing behind them read as gaps rather than
            being silently omitted.
          */
          <ChainRail
            current="clientInvoice"
            hideEmpty
            rfq={invoice.rfqId}
            rfqNumber={invoice.rfqNumber}
            quotation={invoice.quotationId}
            quotationNumber={invoice.quotationNumber}
            confirmedOrder={invoice.confirmedOrderId}
            purchaseOrders={chainPurchaseOrders}
            vendorInvoices={chainVendorInvoices}
            clientInvoice={invoice._id}
            clientInvoiceNumber={invoice.invoiceNumber}
            onNavigate={guardLeave}
          />
        }
        total={money(summary.invoiceGrandTotal, currency)}
        marginPct={progress.marginPct} /* D183.3: the ACTUAL margin (billed and entered lines); the stored summary stays gross */
        marginFinal={progress.final}
        progress={<OrderProgressStrip progress={progress} unavailable={liveAnswered?.data?.unavailable} loadFailed={!!liveAnswered?.failedAt} staleSince={liveAnswered?.data && liveAnswered.readAt ? fmtDateTime(liveAnswered.readAt) : null} currency={currency} />}
        netAfterCredits={creditNotes.length > 0 ? money((summary.invoiceGrandTotal || 0) + creditTotal, currency) : undefined}
        creditStatus={creditStatusFor(summary.invoiceGrandTotal, creditTotal)}
      />
      <div className="px-8 py-4 flex-1 min-h-0 overflow-hidden">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm h-full flex flex-col overflow-hidden no-print">
          <div className="px-4 pt-4 no-print">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 gap-0.5">
              {([
                { key: 'invoice',       label: `Lines ${(invoice.items || []).length}` },
                { key: 'delivery',      label: `Delivery · ${summary.deliverySlipCount ?? 0} slips` },
                { key: 'profitability', label: 'Profitability' },
                { key: 'details',       label: 'Details' },
              ] as { key: TabKey; label: string }[]).map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-4 py-2 text-xs font-semibold rounded-md transition-colors ${
                    tab === key ? 'bg-primary-500 text-white' : 'text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div ref={paneRef} data-testid="invoice-pane" className="flex-1 overflow-auto p-4 pb-24">
            {tab === 'invoice' && (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1 space-y-3">
                    {/* D98.3 - the one issues bar (replaces the verification, validation and discrepancy boxes). */}
                    <IssuesBar
                      result={issues}
                      status={invoice.status}
                      dismissedKeys={dismissedKeys}
                      readOnly={isCancelled || !canEditInvoice}
                      onFilterIssues={(indexes) => setIssueFilter(indexes.length ? indexes : null)}
                      onDismiss={dismissAlert}
                    />

                    {linesLocked && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 no-print">
                        {isCancelled ? 'Cancelled — nothing on this invoice can be changed' : 'Sent — lines are locked; raise a credit note to change what the client owes'}
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-2 no-print">
                      <input
                        aria-label="Search lines"
                        type="search"
                        value={lineSearch}
                        onChange={(e) => setLineSearch(e.target.value)}
                        placeholder="Search item name"
                        className="h-8 w-56 rounded border border-slate-300 px-2 text-xs"
                      />
                      {notSuppliedCount > 0 && (
                        <button
                          type="button"
                          aria-pressed={notSuppliedOnly}
                          onClick={() => setNotSuppliedOnly((v) => !v)}
                          className={`h-8 rounded border px-3 text-xs font-semibold ${notSuppliedOnly ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-slate-300 bg-white text-slate-700'}`}
                        >
                          Not supplied ({notSuppliedCount})
                        </button>
                      )}
                      <span className="flex-1" />
                      {!linesLocked && (
                        <>
                          {/* D157.2b: open to whoever can edit invoices (the routes' gate). */}
                          {canEditInvoice && (
                            <button
                              onClick={openVendorBillModal}
                              className="px-3 py-2 rounded-md border border-primary-500 bg-white text-xs font-semibold text-primary-600"
                            >
                              Import Vendor Bill
                            </button>
                          )}
                          <button
                            onClick={addBlankLineItem}
                            className="px-3 py-2 rounded-md border border-slate-200 bg-white text-xs font-semibold text-slate-700"
                          >
                            Add Blank Line
                          </button>
                        </>
                      )}
                    </div>

                    {/* D170.7: the Not supplied button also opens the list and the captain's print */}
                    {notSuppliedOnly && notSuppliedCount > 0 && <NotSuppliedPanel items={invoice.items || []} onPrint={printNotSupplied} />}

                    <LinesGrid
                      items={invoice.items || []}
                      currency={currency}
                      columnWidths={columnWidths}
                      pricingFor={getRowPricing}
                      severities={lineSeverities}
                      unpickedLines={unpickedLines}
                      selectedIndex={selectedLine}
                      visibleIndexes={visibleIndexes}
                      creditedShortByLine={creditedShortByLine}
                      hidesSupplierBilled={hidesSupplierBilled}
                      linesLocked={linesLocked && !isCancelled}
                      readOnly={isCancelled}
                      updateItem={updateItem}
                      setDone={setDoneTicks}
                      onCommitCost={updateCostPrice}
                      costEntryOf={(item) => costEntryFor(currency, ttdRateFor(item))}
                      ttdCostOf={String(currency).trim().toUpperCase() === 'USD' ? (item) => ttdCostFor(item, getRowPricing(item).costPrice)! : undefined}
                      onCommitPrice={updateSellingPrice}
                      onCommitMarkup={(index, text) => {
                        // D98.11: no confirm box; the "overridden" badge is the record of it.
                        if (!invoice.items[index]?.markupOverridden) updateItem(index, { markupOverridden: true });
                        updateMarkup(index, text);
                      }}
                      removalReason={(item, index) => lineRemovalLock(item, index, creditNotes)}
                      onRemove={removeItem}
                      onOpenLine={setSelectedLine}
                    />
                  </div>
                  {renderDrawer()}
                </div>

                {/* D56 — Subtotal / Discount (x%) / one row per charge / Grand
                    Total, read from the invoice's STORED summary (never
                    recomputed in this block — the same stored figures the
                    server PDF's invoiceTotalRows prints).
                    Charge rows are editable pre-send through the page's
                    existing Save button (buildInvoicePatchPayload); the
                    discount percent has no editor anywhere on this page
                    (copied once at generation, server-enforced). */}
                <InvoiceTotalsBlock
                  invoice={invoice}
                  currency={currency}
                  onChangeCharge={updateCharge}
                  onAddCharge={addCharge}
                  onRemoveCharge={removeCharge}
                />
                {/* D98.9 - the three VAT columns went: USC never charges a vessel VAT (D53). */}
                <p className="text-right text-xs text-slate-500">No VAT: a vessel is never charged VAT.</p>
              </div>
            )}

            {tab === 'delivery' && (
              <DeliveryTab
                items={invoice.items || []}
                deliverySlipSection={deliverySlipSection}
                summary={summary}
                updateItem={updateItem}
                stageLines={stageDispatchQuantities}
                orderItems={confirmedOrder?.confirmedOrder?.items}
                readOnly={!canEditInvoice}
                cancelled={isCancelled}
                sentOnward={isInvoiceSentOnward(invoice.status)}
                creatingSlip={creatingSlip}
                onCreateSlip={createDeliverySlip}
                onPrintSlip={printIndividualSlip}
                onPrintSlipPackingList={printSlipPackingList}
                onPrintPackingList={printPackingList}
                onExchangeSubmit={exchangeSlip}
                exchangeBusy={busyAction !== null}
                creditNotes={creditNotes}
                customs={invoice.category === CUSTOMS_CATEGORY}
                onPrintCustomsSlip={printCustomsSlip}
              />
            )}

            {/* Kept mounted (hidden) off-tab: a typed date the box refused lives only in
                its text, so unmounting it would silently drop the refusal and let Save
                send the previous date. */}
            <div hidden={tab !== 'details'}>
              <DetailsTab
                invoice={invoice}
                setInvoice={(next: ClientInvoiceDto, typedField?: string) => editInvoice(next, typedField ? `details:${typedField}` : undefined)}
                readOnly={!canEditInvoice || isCancelled}
                creditNotes={creditNotes.map((cn) => ({ ...cn, _id: String(cn._id) }))}
                grandTotal={summary.invoiceGrandTotal}
                creditTotal={creditTotal}
                currency={currency}
                onInvoiceDateValidityChange={setInvoiceDateValid}
                onOpenCreditNote={async (id) => { if (isDirty && !(await askDiscard())) return; navigate(`/credit-notes/${id}`); }}
              />
            </div>

            {tab === 'profitability' && (
              <ProfitabilityTab progress={progress} summary={{ marginSanityWarning: summary.marginSanityWarning }} currency={currency} invoiceId={invoice._id} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// D56 — Subtotal / Discount (x%) / one row per charge / GRAND TOTAL. The row
// LIST comes from buildInvoiceTotalsRows (features/clientInvoice/invoiceTotals.ts),
// reading the invoice's stored summary — this component adds no arithmetic of
// its own, only the editable inputs on the charge rows. Charges are editable
// while `canEditInvoiceCharges(invoice)` — not yet sent-onward AND not an
// ADDITIONAL invoice (D56 point 3: one bills at full price, with no discount
// and no charges, ever — the server refuses a PATCH that tries either with
// CLIENT_INVOICE_CHARGES_NOT_ON_ADDITIONAL, so the page does not offer to).
// The discount percent is never editable here regardless (patchClientInvoice's
// allow-list omits it on purpose — copied once at generation from the
// confirmed order).
function InvoiceTotalsBlock({
  invoice,
  currency,
  onChangeCharge,
  onAddCharge,
  onRemoveCharge,
}: {
  invoice: ClientInvoiceDto;
  currency: string;
  onChangeCharge: (index: number, patch: Partial<InvoiceChargeRow>) => void;
  onAddCharge: () => void;
  onRemoveCharge: (index: number) => void;
}) {
  const summary: any = invoice.summary || {};
  const charges = invoice.charges || [];
  const editable = canEditInvoiceCharges(invoice);
  const rows = buildInvoiceTotalsRows({
    quotedTotal: summary.quotedTotal,
    discountPercentage: invoice.discountPercentage,
    discountAmount: summary.discountAmount,
    charges,
    invoiceGrandTotal: summary.invoiceGrandTotal,
  });

  return (
    <div className="no-print rounded-md border border-slate-200 bg-white p-4" data-testid="invoice-totals-block">
      <div className="text-sm font-semibold text-slate-800 mb-2">Totals</div>
      <div className="space-y-1.5 text-xs">
        {rows.map((row) => {
          if (row.kind === 'charge' && editable) {
            const charge = charges[row.index] || { label: '', amount: 0 };
            return (
              <div key={row.key} className="flex items-center gap-2">
                <input
                  value={charge.label}
                  onChange={(e) => onChangeCharge(row.index, { label: e.target.value })}
                  placeholder="Charge label"
                  aria-label={`Charge ${row.index + 1} label`}
                  className="flex-1 h-7 px-2 rounded border border-slate-200 bg-white text-xs"
                />
                <DraftInput
                  inputMode="decimal"
                  value={formatEditableNumber(charge.amount)}
                  onCommit={(text) => {
                    // blank is 0 (a charge row always has an amount); junk keeps the stored amount
                    const parsed = parseCellText(text);
                    if (parsed.kind !== 'junk') onChangeCharge(row.index, { amount: parsed.kind === 'blank' ? 0 : parsed.value });
                  }}
                  aria-label={`Charge ${row.index + 1} amount`}
                  className="w-28 h-7 px-2 rounded border border-slate-200 bg-white text-xs text-right"
                />
                <button
                  type="button"
                  onClick={() => onRemoveCharge(row.index)}
                  className="text-rose-600 text-xs font-semibold px-1"
                  title="Remove this charge"
                >
                  Remove
                </button>
              </div>
            );
          }
          const isGrandTotal = row.kind === 'grandTotal';
          return (
            <div
              key={row.key}
              className={`flex items-center justify-between ${
                isGrandTotal ? 'pt-1.5 mt-1 border-t border-slate-300 font-semibold text-slate-900' : 'text-slate-700'
              }`}
            >
              <span>{row.label}</span>
              <span>
                {row.kind === 'discount' ? '-' : ''}
                {money(row.amount, currency)}
              </span>
            </div>
          );
        })}
      </div>
      {editable ? (
        <div className="mt-2.5">
          <button
            type="button"
            onClick={onAddCharge}
            className="px-2 py-1 rounded border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            + Add Charge
          </button>
        </div>
      ) : invoice.isAdditional ? (
        // D56 point 3 — an additional invoice never HAS a discount/charge to
        // show a lock note about (the server never sets either at
        // generation, and a later PATCH adding one is refused too), so this
        // is shown unconditionally rather than gated on charges.length like
        // the sent-lock note below — there is nothing to check for.
        <div className="mt-2.5 text-xs text-slate-500">
          This is an additional invoice — it bills at full price, with no discount and no charges.
        </div>
      ) : charges.length > 0 || Number(invoice.discountPercentage) > 0 ? (
        <div className="mt-2.5 text-xs text-slate-500">
          This invoice has been {invoice.status} — charges are locked. Raise a credit note to adjust what the client owes.
        </div>
      ) : null}
    </div>
  );
}



